// vzhlad_predajne_v2 (20. 10. 2026, ústava 61.10): starý oranžový panel/akcent nahradený hodnotami témy — 1 miest
// PokladnaView.jsx — Matrix v2 Pokladňa + Toner (ústava 11 + Tajný fond)
// 2 taby: Pokladňa (cash firmy) · 🔒 Toner (súkromné mimo firmy)
// Backend: /api/pokladna, /api/pokladna/stats, /api/toner, /api/toner/balance
import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useDirtyFlag } from '../hooks/useDirtyState.js'   // straz_v4 (29. 9. 2026): rozpísaný formulár drží spoločnú stráž odchodu (ústava 53.1.b)
import { strazKlik } from '../hooks/unsavedGuard.js'   // straz_v1: riadok tabuľky sa pred prepnutím spýta na neuložené zmeny (ústava 53.1)
import useLiveRefresh from '../hooks/useLiveRefresh.js'
import { COLORS, TYPO, SPACE, RADIUS, SK, todayLocal, norm } from '../theme.js'
import { PageHeader, KpiRow, KpiCard, FilterChips, SearchInput, Tabs, SearchableSelect, MasterDetail, ListRow, ListStatusBar, EmptyPane } from '../components/Ui.jsx'
import { Btn, BtnAdd, BtnGhost } from '../components/Btn.jsx'
import { Modal } from '../components/Modal.jsx'
import { fetchAuth } from '../auth/fetchAuth.js'
import { useToast } from '../components/Toast.jsx'
import { usePinGate } from '../hooks/usePinGate.jsx'
import HelpHint from '../components/HelpHint.jsx'
import VydavokBezDokladuModal from '../components/VydavokBezDokladuModal.jsx'   // 13. 9. 2026 (bod 6): ručný výdavok bez dokladu s dôvodom, PIN dodatočne
import { useAuth } from '../auth/AuthContext.jsx'
import { useLock } from '../hooks/useLock.js'
import RowMenu from '../components/RowMenu.jsx'
import ExportMenu from '../components/ExportMenu.jsx'
import useSortable from '../hooks/useSortable.js'
import { useShortcuts } from '../hooks/useShortcuts.js'
import { useListKeyboard } from '../hooks/useListKeyboard.js'   // V3 131.4
import { useConfirm } from '../components/ConfirmDialog.jsx'
import { useNavigate, useSearchParams } from 'react-router-dom'
import AtmVyberyTlacidlo from '../components/AtmVybery.jsx'   // HOTOVOST_VAZBA_V1 (Mirko 2317): výbery z bankomatu, mimo kasy
import { PRINCIP_POKLADNE } from '../components/HotovostVazba.jsx'   // HOTOVOST_VAZBA_V1: Princíp pokladne pod otáznikom (Mirko 2312)

const ROLES_POKLADNA = ['admin','owner','owner_view','office_manager','accountant']
// POKLADNA_ZAKAZKA_V1 (20. 10. 2026, Mirko: „lucia si je zodpovedna za pokladnu“): Toner vidí a vedie aj vedúca office — ústava 11.11 admin + office_manager + majiteľ
const ROLES_TONER    = ['admin','owner','office_manager']

// COST_CATS — kategórie podľa strediska (ústava 11.7 + starý BlockyView)
const COST_CATS = {
  rezia: [
    'Nákup materiálu / spotrebného tovaru',
    'PHM (pohonné hmoty)',
    'Nákup náradia / zariadenia',
    'Oprava náradia / servis',
    'Prenájom náradia',
    'Potraviny / stravovanie',
    'Kancelárske potreby',
    'Čistiace a hygienické prostriedky',
    'Oblečenie / pracovné pomôcky',
    'Parkovné / diaľničné',
    'Mzdy a odmeny',
    'Účtovníctvo',
    'Bankové poplatky',
    'Dane a poplatky',
    'Poistenie',
    'Reklama a marketing',
    'IT a technika',
    'BOZP',
    'Právne služby',
    'Prenájom priestorov',
    'Energie a médiá',
    'Iné réžijné výdavky',
  ],
  zakazka: [
    'Stavebný materiál',
    'PHM (pohonné hmoty — zákazka)',
    'Nákup náradia / zariadenia',
    'Oprava náradia / servis',
    'Prenájom náradia',
    'Nákup pre zákazku',
    'Doprava na zákazku',
    'Mzdy a odmeny',
    'Subdodávka práce',
    'Zriadenie staveniska',
    'Iný zákazkový výdavok',
  ],
}
// Pôvodný flat list pre legacy záznamy
const KATEGORIE = [
  'mzda', 'material', 'phm', 'leasing', 'atm', 'ekasa',
  'rd_platba', 'ostatné', 'ine',
]

// DRUH_POHYBU_v1 — druhy pohybov v pokladni (ústava 11.0 — formát vlákna)
// POKLADNA_ZAKAZKA_V1 (Mirko 20. 10. 2026: „a,b,c a este presun z tonera a do tonera“): príjem je (a) platba klienta so zákazkou,
// (b) presun (e-Kasa, ATM / banka, toner — nie je príjem, výdaj ani réžia), (c) iný príjem; výdaj ide na zákazku, do réžie alebo je presun.
const DRUHY_POHYBOV = [
  // PRÍJMY
  { kod: 'rd_platba',  smer: 'prijem', label: 'Platba klienta (RD vklad)',          hint: 'Zákazka povinná, ráta sa do príjmu zákazky (ústava 11.8)' },
  { kod: 'vklad_banka',smer: 'prijem', label: 'Presun — výber z banky / ATM',        hint: 'Presun: nie je príjem ani réžia' },
  { kod: 'ekasa',      smer: 'prijem', label: 'Presun — výber z e-Kasy (predajňa)',  hint: 'Presun: nie je príjem ani réžia' },
  { kod: 'z_tonera',   smer: 'prijem', label: 'Presun — z tonera',                   hint: 'V Toneri vznikne výber, prepojený s týmto pohybom (ústava 11.11)', toner: true },
  { kod: 'iny_prijem', smer: 'prijem', label: 'Iný príjem',                          hint: 'Réžia alebo zákazka' },
  // VÝDAVKY
  // ZAMOK_11_2 (13.9.2026, Mirko GO): mzda / záloha / nafta partii sa v Pokladni NEZAPISUJÚ — len cez Mzdy › Vyplatiť; doklad vznikne sám.
  // Druh 'mzda' ostáva len na zobrazenie starých záznamov (legacy: true), v novom pohybe sa neponúka.
  { kod: 'mzda',       smer: 'vydaj',  label: 'Mzda (len staré záznamy)',       hint: 'Nové výplaty sa zapisujú v Mzdách › Vyplatiť (ústava 11.2)', legacy: true },
  { kod: 'uhrada_fa',  smer: 'vydaj',  label: 'Úhrada Došlej FA (cash ≤ €400)',hint: 'FA povinná (ústava 11.10)' },
  { kod: 'blocok',     smer: 'vydaj',  label: 'Bloček / nákup cash',           hint: 'Zákazka alebo réžia a kategória povinné' },
  { kod: 'iny_vydaj',  smer: 'vydaj',  label: 'Iný výdavok',                   hint: 'Zákazka alebo réžia povinná' },
  { kod: 'do_ekasy',   smer: 'vydaj',  label: 'Presun — vklad do e-Kasy',      hint: 'Presun: nie je výdavok ani réžia' },
  { kod: 'do_tonera',  smer: 'vydaj',  label: 'Presun — do tonera',            hint: 'V Toneri vznikne vklad, prepojený s týmto pohybom (ústava 11.11)', toner: true },
]
const DRUH_KATEGORIA = { rd_platba: 'rd_platba', vklad_banka: 'vklad_banka', ekasa: 'ekasa', z_tonera: 'toner', do_tonera: 'toner', do_ekasy: 'ekasa', iny_prijem: 'iny_prijem', mzda: 'mzda' }
const DRUH_PRESUN = ['vklad_banka', 'ekasa', 'z_tonera', 'do_tonera', 'do_ekasy']
const PRESUN_KAT = ['ekasa', 'atm', 'vklad_banka', 'toner']
const PRESUN_NAZOV = { ekasa: 'e-Kasa', atm: 'ATM', vklad_banka: 'banka / ATM', toner: 'toner' }
function druhZoZaznamu(r) {
  const k = String(r.kategoria || '').toLowerCase(), prijem = String(r.typ || 'prijem').toLowerCase() === 'prijem'
  if (k === 'mzda') return 'mzda'
  if (k === 'rd_platba' && prijem) return 'rd_platba'
  if (k === 'toner') return prijem ? 'z_tonera' : 'do_tonera'
  if (k === 'ekasa') return prijem ? 'ekasa' : 'do_ekasy'
  if ((k === 'vklad_banka' || k === 'atm') && prijem) return 'vklad_banka'
  return prijem ? 'iny_prijem' : 'iny_vydaj'
}
// „aktuálny deň“ = deň, keď pohyb v Matrixe vznikol (Bratislava); riadky z importu zo Sheetu sú vždy staré — rovnako ako server (11.12)
const dnesBA = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Bratislava' })
function vznikolDnes(r) {
  const t = String((r && r.created_at) || ''); if (!t) return false
  const d = new Date(/[TZ]/.test(t) ? t : t.replace(' ', 'T') + 'Z'); if (isNaN(d)) return false
  return d.toLocaleDateString('sv-SE', { timeZone: 'Europe/Bratislava' }) === dnesBA() && !/^\[Sheet 26-POKLADŇA/.test(String(r.poznamka || ''))
}
// Partner alebo osoba — jeden výber z dvoch registrov (karta zamestnanca + Partneri, 0.J); hodnota „E:id“ alebo „P:id“
function moznostiOsob(zamestnanci, partneri) {
  const z = (zamestnanci || []).filter(e => !(e.active === 0 || e.active === '0' || e.active === false)).map(e => ({ value: 'E:' + e.id, label: (e.name || `${e.firstName || ''} ${e.lastName || ''}`.trim() || e.id) + ' · zamestnanec' }))
  const p = (partneri || []).map(c => ({ value: 'P:' + c.id, label: (c.name || c.id) + ' · partner' + (c.ico ? ' · IČO ' + c.ico : '') }))
  return [...z, ...p].sort((a, b) => a.label.localeCompare(b.label, 'sk'))
}
const poleZoznamu = (d) => Array.isArray(d) ? d : ((d && (d.rows || d.customers || d.employees)) || [])
function druhInfo(kod) { return DRUHY_POHYBOV.find(d => d.kod === kod) || null }
const MESIACE = ['Jan','Feb','Mar','Apr','Máj','Jún','Júl','Aug','Sep','Okt','Nov','Dec']

// ── ZÁLOHY NA NÁKUP — tlačidlo v Pokladni (p98; Mirko 21. 9. 2026: „Zálohy na nákup musí byť tlačidlo v pokladni…
// Vydáva len ten, kto má oprávnenie na pokladňu — aktuálne iba vedúci office. V prípade dovolenky vedúcej office admin,
// majiteľ. Nikto iný. Zálohu berie iba zamestnanec, takže tam chýba aj číselník."). Ústava 135.4: peniaze odchádzajú
// z pokladne raz, pri vydaní; bloček zálohu uzavrie. Widget na úvodnej obrazovke už len informuje (0.W).
function ZalohyNakupTlacidlo() {
  const toast = useToast()
  const [open, setOpen] = useState(false)
  const [data, setData] = useState(null)
  const [zam, setZam] = useState([])
  const [novy, setNovy] = useState({ employee_id: '', suma: '', ucel: '', poznamka: '' })
  const _s0 = useRef(null); if (_s0.current === null) _s0.current = JSON.stringify(novy); useDirtyFlag(JSON.stringify(novy) !== _s0.current, { key: 'pokladna-zaloha-nakup', discard: () => { setNovy({ employee_id: '', suma: '', ucel: '', poznamka: '' }) } })   // straz_v4
  const [busy, setBusy] = useState(false)
  const nacitaj = () => fetchAuth('/api/zalohy-nakup').then(r => (r.ok ? r.json() : null)).then(d => { if (d) setData(d) }).catch(() => {})
  useEffect(() => { nacitaj() }, [])
  useEffect(() => {
    if (!open || zam.length) return
    fetchAuth('/api/employees').then(r => (r.ok ? r.json() : [])).then(d => {
      const a = (Array.isArray(d) ? d : (d && (d.rows || d.employees)) || []).filter(e => !(e.active === 0 || e.active === '0' || e.active === false))
      setZam(a.map(e => ({ value: String(e.id), label: String(e.name || [e.firstName, e.lastName].filter(Boolean).join(' ') || e.id) }))
        .sort((x, y) => x.label.localeCompare(y.label, 'sk')))
    }).catch(() => {})
  }, [open])   // eslint-disable-line react-hooks/exhaustive-deps
  const pocet = (data && data.pocet) || 0
  const suma = Number((data && data.suma) || 0)
  const smiem = !!(data && data.smiemVydat)
  const cislo = (s) => Number(String(s || '').replace(',', '.'))
  const vydaj = async () => {
    if (!novy.employee_id) { toast.error('Vyber zamestnanca zo zoznamu — zálohu berie iba zamestnanec.'); return }
    if (!(cislo(novy.suma) > 0)) { toast.error('Zadaj sumu väčšiu ako nula.'); return }
    if (!novy.ucel.trim()) { toast.error('Na čo je záloha? Bez účelu sa nedá vyúčtovať.'); return }
    setBusy(true)
    try {
      const r = await fetchAuth('/api/zalohy-nakup', { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employee_id: novy.employee_id, suma: cislo(novy.suma), ucel: novy.ucel.trim(), poznamka: novy.poznamka.trim() }) })
      const j = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(j.error || 'Zálohu sa nepodarilo vydať.')
      toast.success('Záloha vydaná — výdaj je zapísaný v pokladni.')
      setNovy({ employee_id: '', suma: '', ucel: '', poznamka: '' })
      await nacitaj()
    } catch (e) { toast.error(e.message) } finally { setBusy(false) }
  }
  const vrat = async (z) => {
    if (!window.confirm('Záloha ' + Number(z.suma).toFixed(2) + ' € — ' + z.osoba + ' sa vrátila v hotovosti (nákup sa neuskutočnil)?\n\nDo pokladne pribudne príjem.')) return
    setBusy(true)
    try {
      const r = await fetchAuth('/api/zalohy-nakup/' + z.id + '/vratit', { method: 'POST' })
      const j = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(j.error || 'Nepodarilo sa.')
      toast.success('Vrátené — príjem je zapísaný v pokladni.')
      await nacitaj()
    } catch (e) { toast.error(e.message) } finally { setBusy(false) }
  }
  const pole = { width: '100%', padding: '8px 10px', border: '1px solid ' + COLORS.line, borderRadius: RADIUS.sm, fontFamily: 'inherit', fontSize: TYPO.sizes.sm, boxSizing: 'border-box' }
  const lab = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: TYPO.sizes.xs, color: COLORS.muted }
  return (
    <>
      <BtnGhost onClick={() => setOpen(true)}
        title="Hotovosť vydaná dopredu na nákup, ku ktorej ešte nie je bloček (ústava 135.4). Vydáva vedúca office, počas jej dovolenky admin alebo majiteľ.">
        Zálohy na nákup{pocet ? ' · ' + pocet + ' · ' + suma.toFixed(0) + ' €' : ''}
      </BtnGhost>
      <Modal open={open} onClose={() => setOpen(false)} title="Zálohy na nákup" maxWidth={700} width="min(700px, 96vw)"
        subtitle={pocet + ' otvorených, spolu ' + suma.toFixed(2) + ' € — peniaze už z pokladne odišli, bloček zálohu uzavrie a vyrovná sa iba rozdiel'}
        footer={<BtnGhost onClick={() => setOpen(false)}>Zavrieť</BtnGhost>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.md }}>
          <div>
            {pocet === 0 ? <div style={{ color: COLORS.add, fontWeight: TYPO.weights.bold }}>Všetky zálohy sú vyúčtované.</div>
              : (data.polozky || []).map(z => (
                <div key={z.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid ' + COLORS.lineSoft, flexWrap: 'wrap' }}>
                  <span style={{ flex: '1 1 140px', fontWeight: TYPO.weights.bold }}>{z.osoba}</span>
                  <span style={{ flex: '1 1 160px', color: COLORS.muted }}>{z.ucel}</span>
                  <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: TYPO.weights.bold }}>{Number(z.suma).toFixed(2)} €</span>
                  <span style={{ fontSize: TYPO.sizes.xs, color: z.dni > 7 ? COLORS.remove : COLORS.muted, minWidth: 74, textAlign: 'right' }}>{z.dni === 0 ? 'dnes' : 'pred ' + z.dni + ' dňami'}</span>
                  {smiem && <BtnGhost onClick={() => vrat(z)} disabled={busy} title="Peniaze sa vrátili v hotovosti, nákup sa neuskutočnil — do pokladne pribudne príjem">Vrátené</BtnGhost>}
                </div>
              ))}
          </div>
          {smiem ? (
            <div style={{ border: '1px solid ' + COLORS.line, borderRadius: RADIUS.md, padding: SPACE.md, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: SPACE.sm, alignItems: 'end' }}>
              <div style={{ gridColumn: '1 / -1', fontWeight: TYPO.weights.bold }}>Vydať zálohu na nákup</div>
              <div style={lab}>Zamestnanec *
                <SearchableSelect value={novy.employee_id} onChange={v => setNovy(n => ({ ...n, employee_id: v }))} menuPortal
                  options={zam} placeholder="Hľadať zamestnanca…" />
              </div>
              <label style={lab}>Suma € *<input value={novy.suma} onChange={e => setNovy(n => ({ ...n, suma: e.target.value }))} inputMode="decimal" placeholder="napr. 50" style={pole} /></label>
              <label style={lab}>Na čo *<input value={novy.ucel} onChange={e => setNovy(n => ({ ...n, ucel: e.target.value }))} placeholder="napr. nákup lepidla" style={pole} /></label>
              <label style={lab}>Poznámka<input value={novy.poznamka} onChange={e => setNovy(n => ({ ...n, poznamka: e.target.value }))} style={pole} /></label>
              <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: SPACE.sm, flexWrap: 'wrap' }}>
                <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>Z pokladne odíde výdaj hneď teraz. Bloček zálohu neskôr uzavrie.</span>
                <BtnAdd onClick={vydaj} disabled={busy}>{busy ? 'Ukladám…' : 'Vydať zálohu'}</BtnAdd>
              </div>
            </div>
          ) : (
            <div style={{ fontSize: TYPO.sizes.sm, color: COLORS.muted }}>Zálohu vydáva a vracia len vedúca office; počas jej dovolenky admin alebo majiteľ.</div>
          )}
        </div>
      </Modal>
    </>
  )
}

export default function PokladnaView() {
  const { user } = useAuth()
  const [tab, setTab] = useState('pokladna')
  // POKLADNA_ZAKAZKA_V1 (Mirko 20. 10. 2026: „chýba mi tlačidlo TONER(bude farebne) nech vidím zostatok“)
  const smieTonerHore = !!user && ROLES_TONER.includes(user.role)
  const [tonerZost, setTonerZost] = useState(null)
  const nacitajToner = () => { if (smieTonerHore) fetchAuth('/api/toner/balance').then(r => r.ok ? r.json() : null).then(d => { if (d) setTonerZost(Number(d.zostatok) || 0) }).catch(() => {}) }
  useEffect(nacitajToner, [smieTonerHore, tab])   // eslint-disable-line react-hooks/exhaustive-deps

  if (!user || !ROLES_POKLADNA.includes(user.role)) {
    return (
      <div style={{ padding: SPACE.xl, color: COLORS.muted }}>
        Nemáš oprávnenie zobraziť Pokladňu.
      </div>
    )
  }

  const canSeeToner = ROLES_TONER.includes(user.role)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <PageHeader title="Pokladňa" subtitle="Kniha pokladne: príjmy, výdavky a bežný zostatok · ústava 11 a 131">
        <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: TYPO.sizes.xs, color: COLORS.muted }}>Princíp pokladne<HelpHint section="11.16" text={PRINCIP_POKLADNE} /></span>
        <ZalohyNakupTlacidlo />
        {['admin', 'owner', 'office_manager', 'accountant'].includes(user.role) && <AtmVyberyTlacidlo />}
        {canSeeToner && (
          <button type="button" onClick={() => setTab(tab === 'toner' ? 'pokladna' : 'toner')}
            title="Toner — súkromný fond mimo firmy (ústava 11.11). Klik otvorí Toner, ďalší klik vráti Pokladňu."
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '5px 14px', border: 0, borderRadius: RADIUS.sm, background: 'linear-gradient(135deg, #B45309, #D97706)', color: '#fff', fontFamily: 'inherit', fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.extra, letterSpacing: '0.04em', cursor: 'pointer', outline: tab === 'toner' ? '2px solid #92400E' : 'none', outlineOffset: 2 }}>
            TONER <span style={{ fontFamily: TYPO.mono, fontWeight: TYPO.weights.semibold, letterSpacing: 0, background: 'rgba(255,255,255,0.18)', padding: '1px 8px', borderRadius: 4 }}>{tonerZost == null ? '…' : SK.eur(tonerZost)}</span>
          </button>
        )}
      </PageHeader>
      <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: SPACE.lg }}>
        <Tabs value={tab} onChange={setTab} tabs={[
          { key: 'pokladna', label: 'Pokladňa' },
          ...(canSeeToner ? [{ key: 'toner', label: 'Toner' }] : []),
        ]} />
        {tab === 'pokladna' && <PokladnaTab onZmena={nacitajToner} />}
        {tab === 'toner'    && canSeeToner && <TonerTab onZmena={nacitajToner} />}
      </div>
    </div>
  )
}

// ─── Tab POKLADŇA ─────────────────────────────────────────────────────
// V3 (131.8): päť stavov → pre Pokladňu tri: čaká na schválenie, bez dokladu, zapísané. Štítok s textom, farba ho len dopĺňa.
const POKL_STAV = {
  caka:        { label: 'Čaká na schválenie', fg: COLORS.total,  bg: COLORS.totalBg },
  bez_dokladu: { label: 'Bez dokladu',        fg: COLORS.remove, bg: COLORS.removeBg },
  ok:          { label: 'Zapísané',           fg: COLORS.add,    bg: COLORS.addBg },
}
function PoklStavTag({ stav }) {
  const s = POKL_STAV[stav] || POKL_STAV.ok
  return <span style={{ display: 'inline-flex', alignItems: 'center', height: 20, padding: '0 8px', borderRadius: 10, fontSize: 11, fontWeight: TYPO.weights.bold, color: s.fg, background: s.bg, whiteSpace: 'nowrap' }}>{s.label}</span>
}
const bezMenyP = (v) => SK.eur(v).replace(' €', '')

// V3 Pokladňa (Mirko 15. 9. 2026 „mal si jej dať aj nový vzhľad“): kniha pokladne so stĺpcami 131.3, prepínač Karty / Tabuľka (131.2),
// klávesnica (131.4), tri bodky bez „pripravujeme“, zámok výplat 11.2 ako nastavenie (súbeh so Sheetom). Toner s detailom len pre majiteľa (11.11).
function PokladnaTab({ onZmena } = {}) {
  const toast = useToast()
  const confirm = useConfirm()
  const navigate = useNavigate()
  const { user: meU } = useAuth()
  const role = (meU && meU.role) || ''
  const canSeeToner = ROLES_TONER.includes(role)
  const smieSchvalit = ['admin', 'owner', 'owner_view'].includes(role)
  const smieZamok = ['admin', 'owner'].includes(role)
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [chip, setChip] = useState('vsetky')
  const [filterMonth, setFilterMonth] = useState(null)
  const [viewMode, setViewMode] = useState(() => { try { return localStorage.getItem('pokladna_v3_view') === 'karty' ? 'karty' : 'tabulka' } catch (_) { return 'tabulka' } })
  const [selectedId, setSelectedId] = useState(null)
  const [modal, setModal] = useState(null)  // { record? }
  const [priradit, setPriradit] = useState(null)   // POKLADNA_BEZ_ZAKAZKY_V1: riadok, ktorému office priraďuje zákazku
  const [hladane] = useSearchParams()   // POKLADNA_ZAKAZKA_V1: /pokladna?pk=PK…|zakazka=Z…|filter=… — preklik z Vyúčtovania RD, Termínov a nervovej sústavy
  const urlHotove = useRef(false)
  const [naSchvalenie, setNaSchvalenie] = useState({ count: 0, suma: 0, rows: [] })
  const [vbfOpen, setVbfOpen] = useState(false)
  const [zamok, setZamok] = useState(null)
  const [projekty, setProjekty] = useState([])
  const [zamestnanci, setZamestnanci] = useState([])
  const [partneri, setPartneri] = useState([])   // POKLADNA_ZAKAZKA_V1: register partnerov pre stĺpec Partner alebo osoba
  const pinGateMain = usePinGate()
  const rok = new Date().getFullYear()

  function load() {
    setLoading(true)
    Promise.all([
      fetchAuth(`/api/pokladna?rok=${rok}`).then(r => r.ok ? r.json() : []),
      fetchAuth('/api/projects').then(r => r.ok ? r.json() : []),
      fetchAuth('/api/pokladna/na-schvalenie').then(r => r.ok ? r.json() : { count: 0, suma: 0, rows: [] }),
      fetchAuth('/api/employees').then(r => r.ok ? r.json() : []).catch(() => []),
      fetchAuth('/api/pokladna/nastavenie').then(r => r.ok ? r.json() : null).catch(() => null),
      fetchAuth('/api/customers').then(r => r.ok ? r.json() : []).catch(() => []),
    ]).then(([rows, pr, ns, emp, nast, cust]) => {
      setPartneri(poleZoznamu(cust))
      setItems(Array.isArray(rows) ? rows : [])
      setProjekty(Array.isArray(pr) ? pr : ((pr && pr.rows) || []))
      setNaSchvalenie(ns || { count: 0, suma: 0, rows: [] })
      setZamestnanci(Array.isArray(emp) ? emp : ((emp && emp.rows) || []))
      setZamok(nast && typeof nast.zamok_11_2 === 'boolean' ? nast.zamok_11_2 : null)
    }).finally(() => { setLoading(false); if (onZmena) onZmena() })   // POKLADNA_ZAKAZKA_V1: TONER hore sa obnoví po každej zmene
  }
  useEffect(load, [rok])
  useLiveRefresh(load, () => !modal && !vbfOpen && !priradit)  // živé; nerušiť keď je otvorené okno

  const projectMap = useMemo(() => { const m = {}; for (const pr of projekty) m[pr.id] = pr.name || pr.id; return m }, [projekty])
  const menoMap = useMemo(() => { const m = {}; for (const e of zamestnanci) m[e.id] = e.name || `${e.firstName || ''} ${e.lastName || ''}`.trim(); return m }, [zamestnanci])
  const partnerMap = useMemo(() => { const m = {}; for (const c of partneri) m[c.id] = c.name || c.id; return m }, [partneri])
  const jeToner = (r) => String(r.kategoria || '').toLowerCase() === 'toner' || /toner/i.test(String(r.popis || '') + ' ' + String(r.poznamka || ''))
  const skryty = (r) => jeToner(r) && !canSeeToner
  const jePrijem = (r) => String(r.typ || '').toLowerCase() === 'prijem'
  const jeOsobe = (r) => ['mzda', 'zaloha', 'záloha', 'nafta', 'vyplata', 'výplata'].includes(String(r.kategoria || '').toLowerCase()) || !!r.employee_id
  const zoSheetu = (r) => /^\[Sheet 26-POKLADŇA/.test(String(r.poznamka || ''))
  const zMiezd = () => false   // HOTOVOST_VAZBA_V1: pohyb v Pokladni je pohyb Lucie aj keď je napojený na Mzdy — mení ho ona (v deň zápisu), starší admin
  const stavRiadku = (r) => r.schvalene === 0 ? 'caka' : (Number(r.bez_dokladu) === 1 ? 'bez_dokladu' : 'ok')
  // POKLADNA_ZAKAZKA_V1: partner alebo osoba z registra; chýbajúci = „doplniť“ (dopĺňa office)
  const osobaText = (r) => r.partner_id ? (partnerMap[r.partner_id] || r.partner_id) : (menoMap[r.employee_id] || r.employee_id || '')
  const bezOsoby = (r) => !r.partner_id && !r.employee_id && !skryty(r)
  const jePresun = (r) => r.stredisko === 'presun' || (!r.stredisko && !r.project_id && PRESUN_KAT.includes(String(r.kategoria || '').toLowerCase()))
  // POKLADNA_BEZ_ZAKAZKY_V1 (20. 10. 2026, Mirko: „tu chýba pole zákazka“, Kronika pokladna 2157): prázdna zákazka sa ukazovala
  // ako „Réžia“, hoci tabuľka pokladna réžiu vôbec neukladá — 107 riadkov z importu 20. 10. tak klamalo. Teraz „bez zákazky“
  // a office zákazku priradí priamo v zozname. Zapisuje len človek (DOPLNOK 20.10.2026 POKLADŇA NAOSTRO).
  const zakazkaText = (r) => r.project_id ? (r.project_id + (projectMap[r.project_id] ? ' · ' + projectMap[r.project_id] : ''))
    : r.stredisko === 'rezia' ? 'Réžia' : jePresun(r) ? 'presun · ' + (PRESUN_NAZOV[String(r.kategoria || '').toLowerCase()] || r.kategoria) : 'bez zákazky'
  const bezZakazky = (r) => !r.project_id && r.stredisko !== 'rezia' && !jePresun(r) && !skryty(r)
  const jeRdBez = (r) => bezZakazky(r) && jePrijem(r) && String(r.kategoria || '').toLowerCase() === 'rd_platba'
  const smiePriradit = ['admin', 'owner', 'office_manager', 'accountant'].includes(role)
  const osobaBunka = (r) => {
    const t = osobaText(r); if (t) return t
    if (zMiezd(r) || !smiePriradit || skryty(r)) return ''
    return <button type="button" title="Doplniť partnera alebo osobu z registra" onClick={e => { e.stopPropagation(); setPriradit(r) }}
      style={{ border: 0, background: 'none', padding: 0, color: '#C2410C', fontStyle: 'italic', fontFamily: 'inherit', fontSize: TYPO.sizes.xs, cursor: 'pointer' }}>doplniť</button>
  }
  useEffect(() => {
    if (loading || urlHotove.current) return
    const pk = hladane.get('pk'), f = hladane.get('filter'), z = hladane.get('zakazka')
    if (!pk && !f && !z) return
    urlHotove.current = true
    if (f && ['bez_zak', 'rd_bez', 'bez_osoby', 'prijmy', 'vydaje'].includes(f)) setChip(f)
    if (z) setSearch(z)
    if (pk) {
      const r = items.find(x => x.id === pk)
      if (r) {
        setChip('vsetky'); setFilterMonth(null); setSelectedId(pk)
        if ((bezZakazky(r) || bezOsoby(r)) && smiePriradit && !zMiezd(r)) setPriradit(r)
        setTimeout(() => { const el = document.querySelector('[data-row-id="' + pk + '"]'); if (el) el.scrollIntoView({ block: 'center' }) }, 80)
      }
    }
  }, [loading, items])   // eslint-disable-line react-hooks/exhaustive-deps
  const zakazkaBunka = (r) => {
    if (!r.project_id && !bezZakazky(r)) return <span style={{ color: r.stredisko === 'rezia' ? COLORS.text : COLORS.muted }}>{zakazkaText(r)}</span>
    if (r.project_id) return <button type="button" title="Otvoriť zákazku" onClick={e => { e.stopPropagation(); navigate('/zakazky/' + encodeURIComponent(r.project_id)) }}
      style={{ border: 0, background: 'none', padding: 0, color: COLORS.nav, fontWeight: TYPO.weights.semibold, fontFamily: 'inherit', fontSize: 'inherit', cursor: 'pointer', textAlign: 'left' }}>{zakazkaText(r)}</button>
    if (zMiezd(r) || !smiePriradit) return <span title={zMiezd(r) ? 'Pohyb z Miezd — mení sa v Mzdách' : ''} style={{ color: COLORS.total }}>bez zákazky</span>
    return <button type="button" title="Priradiť zákazku" onClick={e => { e.stopPropagation(); setPriradit(r) }}
      style={{ display: 'inline-flex', alignItems: 'center', padding: '1px 8px', borderRadius: 10, border: '1px solid #FDBA74', background: '#FFF7ED', color: '#C2410C', fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.semibold, fontFamily: 'inherit', cursor: 'pointer', whiteSpace: 'nowrap' }}>bez zákazky · Priradiť</button>
  }
  const dokladText = (r) => zoSheetu(r) ? 'zo Sheetu 26-POKLADŇA' : vazbaRiadku(r)

  const platne = useMemo(() => items.filter(r => r.datum && /^\d{4}-\d{2}-\d{2}/.test(r.datum)), [items])
  const vMesiaci = (r) => !filterMonth || parseInt(r.datum.split('-')[1]) === filterMonth
  const pocty = useMemo(() => {
    const c = { vsetky: 0, prijmy: 0, vydaje: 0, osoby: 0, bez_dokladu: 0, caka: 0, sheet: 0, bez_zak: 0, rd_bez: 0, bez_osoby: 0 }
    platne.forEach(r => {
      if (!vMesiaci(r)) return
      c.vsetky++
      if (jePrijem(r)) c.prijmy++; else c.vydaje++
      if (jeOsobe(r)) c.osoby++
      if (Number(r.bez_dokladu) === 1) c.bez_dokladu++
      if (r.schvalene === 0) c.caka++
      if (zoSheetu(r)) c.sheet++
      if (bezZakazky(r)) c.bez_zak++
      if (jeRdBez(r)) c.rd_bez++
      if (bezOsoby(r)) c.bez_osoby++
    })
    return c
  }, [platne, filterMonth])   // eslint-disable-line react-hooks/exhaustive-deps
  const filtered = useMemo(() => {
    const q = norm(search.trim())
    return platne.filter(r => {
      if (!vMesiaci(r)) return false
      if (chip === 'prijmy' && !jePrijem(r)) return false
      if (chip === 'vydaje' && jePrijem(r)) return false
      if (chip === 'osoby' && !jeOsobe(r)) return false
      if (chip === 'bez_dokladu' && Number(r.bez_dokladu) !== 1) return false
      if (chip === 'caka' && r.schvalene !== 0) return false
      if (chip === 'sheet' && !zoSheetu(r)) return false
      if (chip === 'bez_zak' && !bezZakazky(r)) return false
      if (chip === 'rd_bez' && !jeRdBez(r)) return false
      if (chip === 'bez_osoby' && !bezOsoby(r)) return false
      if (!q) return true
      if (skryty(r)) return 'toner'.includes(q)
      return norm([r.id, r.popis, r.kategoria, r.project_id, projectMap[r.project_id], menoMap[r.employee_id], r.poznamka].join(' ')).includes(q)
    }).sort((a, b) => String(b.datum).localeCompare(String(a.datum)) || String(b.id || '').localeCompare(String(a.id || '')))
  }, [platne, filterMonth, chip, search, projectMap, menoMap, canSeeToner])   // eslint-disable-line react-hooks/exhaustive-deps
  const kpi = useMemo(() => {
    const rozsah = platne.filter(vMesiaci)
    const suc = (arr) => arr.reduce((a, r) => a + Math.abs(Number(r.suma) || 0), 0)
    const prijmy = rozsah.filter(jePrijem), vydaje = rozsah.filter(r => !jePrijem(r))
    const posledny = platne.length ? platne[platne.length - 1] : null   // API vracia poradie datum, id ako prepočet zostatkov
    return { zostatok: posledny ? Number(posledny.zostatok) || 0 : 0, prijem: suc(prijmy), prijemN: prijmy.length, vydaj: suc(vydaje), vydajN: vydaje.length }
  }, [platne, filterMonth])   // eslint-disable-line react-hooks/exhaustive-deps
  const sumy = useMemo(() => ({
    prijem: filtered.filter(jePrijem).reduce((a, r) => a + Math.abs(Number(r.suma) || 0), 0),
    vydaj: filtered.filter(r => !jePrijem(r)).reduce((a, r) => a + Math.abs(Number(r.suma) || 0), 0),
    zostatok: filtered.length ? Number(filtered[0].zostatok) || 0 : 0,
  }), [filtered])   // eslint-disable-line react-hooks/exhaustive-deps
  const selected = useMemo(() => filtered.find(r => r.id && r.id === selectedId) || null, [filtered, selectedId])
  useEffect(() => { if (viewMode === 'karty' && (!selectedId || !filtered.some(r => r.id === selectedId)) && filtered.length) setSelectedId(filtered[0].id || null) }, [filtered, selectedId, viewMode])

  const otvor = (r) => { if (!r || skryty(r)) return; setModal({ record: r }) }
  useListKeyboard({ items: filtered.filter(r => r.id), selectedId, onSelect: setSelectedId, onOpen: () => otvor(selected), enabled: !modal && !vbfOpen })
  useShortcuts(useMemo(() => ([
    { keys: '/', label: 'Hľadať', scope: 'pokladna', handler: () => { const el = document.querySelector('[data-pokladna-search]'); if (el) el.focus() } },
    { keys: 'alt+n', label: 'Nový pohyb', scope: 'pokladna', handler: () => setModal({ record: null }) },
  ]), []))
  const setView = (m) => { setViewMode(m); try { localStorage.setItem('pokladna_v3_view', m) } catch (_) {} }

  const approveVBF = async (r) => {
    const res = await pinGateMain('vydaj_bez_dokladu', { suma: r.suma, popis: r.popis, info: 'Schválenie výdavku bez dokladu' })
    if (!res || !res.ok) return
    try {
      const rr = await fetchAuth('/api/pokladna/' + r.id + '/schvalit', { method: 'POST', headers: { 'X-Pin-Audit-Id': String(res.audit_id || '') } })
      if (!rr.ok) { const d = await rr.json().catch(() => ({})); throw new Error(d.error || rr.status) }
      toast.success('Schválené'); load()
    } catch (e) { toast.error('Chyba: ' + e.message) }
  }
  async function deleteRow(r) {
    if (!r.id) return
    const ok = await confirm({ title: 'Vymazať pohyb z Pokladne?', message: `${SK.date(r.datum)} · ${r.popis} · ${SK.eur(Math.abs(Number(r.suma) || 0))} — zostatky sa prepočítajú.`, confirmLabel: 'Vymazať', danger: true })
    if (!ok) return
    try {
      const res = await fetchAuth(`/api/pokladna/${r.id}`, { method: 'DELETE' })
      if (!res.ok) { let m = 'Vymazanie zlyhalo'; try { const d = await res.json(); m = d.error || m } catch (_) {} throw new Error(m) }
      toast.success('Pohyb vymazaný')
      load()
    } catch (e) { toast.error(e.message || 'Chyba pri mazaní') }
  }
  const prepniZamok = async (zap) => {
    const ok = await confirm({
      title: zap ? 'Zapnúť zámok výplat (ústava 11.2)?' : 'Vypnúť zámok výplat (ústava 11.2)?',
      message: zap ? 'Výplaty a zálohy osobám pôjdu len cez Mzdy › Vyplatiť, v Pokladni sa zapísať nedajú. Zapni, keď Matrix pobeží bez Sheetu.' : 'Počas súbehu so Sheetom sa výplaty a zálohy osobám dajú zapísať aj v Pokladni (rozhodnutie Mirka 15. 9. 2026).',
      confirmLabel: zap ? 'Zapnúť zámok' : 'Vypnúť zámok',
    })
    if (!ok) return
    try {
      const r = await fetchAuth('/api/pokladna/nastavenie', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ zamok_11_2: zap }) })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.error || r.status)
      setZamok(d.zamok_11_2)
      toast.success(d.zamok_11_2 ? 'Zámok 11.2 zapnutý' : 'Zámok 11.2 vypnutý — súbeh so Sheetom')
    } catch (e) { toast.error('Chyba: ' + e.message) }
  }
  const menuFor = (r) => {
    if (!r || skryty(r)) return []
    const mz = zMiezd(r)
    return [
      ...(r.schvalene === 0 && smieSchvalit ? [{ key: 'schvalit', label: 'Schváliť (PIN)', onClick: () => approveVBF(r) }, { separator: true }] : []),
      { key: 'edit', label: mz ? 'Otvoriť (upravuje sa v Mzdách)' : 'Upraviť', disabled: !r.id, onClick: () => otvor(r) },
      ...(mz ? [{ key: 'mzdy', label: 'Otvoriť Mzdy › kniha partie', onClick: () => navigate('/mzdy2026') }] : []),
      { separator: true },
      { key: 'del', label: mz ? 'Vymazať (len v Mzdách)' : (!vznikolDnes(r) && role !== 'admin') ? 'Vymazať (starší pohyb len admin)' : 'Vymazať', danger: true, disabled: !r.id || mz || (!vznikolDnes(r) && role !== 'admin'), onClick: () => deleteRow(r) },
    ]
  }

  const chips = [
    { key: 'vsetky', label: 'Všetky · ' + pocty.vsetky },
    { key: 'prijmy', label: 'Príjmy · ' + pocty.prijmy, color: COLORS.add },
    { key: 'vydaje', label: 'Výdavky · ' + pocty.vydaje, color: COLORS.remove },
    { key: 'bez_zak', label: 'Bez zákazky · ' + pocty.bez_zak, color: COLORS.total },
    { key: 'rd_bez', label: 'RD bez zákazky · ' + pocty.rd_bez, color: COLORS.remove },
    { key: 'bez_osoby', label: 'Bez partnera alebo osoby · ' + pocty.bez_osoby, color: COLORS.total },
    { key: 'osoby', label: 'Výplaty osobám · ' + pocty.osoby, color: COLORS.nav },
    { key: 'bez_dokladu', label: 'Bez dokladu · ' + pocty.bez_dokladu, color: COLORS.remove },
    { key: 'caka', label: 'Čaká na schválenie · ' + pocty.caka, color: COLORS.total },
    { key: 'sheet', label: 'Zo Sheetu · ' + pocty.sheet, color: COLORS.muted },
  ]
  const mesiace = [{ key: null, label: 'Celý rok ' + rok, color: COLORS.muted }, ...MESIACE.map((m, i) => ({ key: i + 1, label: m, color: COLORS.muted }))]
  const prazdne = (text) => <div style={{ padding: SPACE.xl, color: COLORS.muted, textAlign: 'center' }}>{text}</div>
  const pozadie = (r, sel) => sel ? COLORS.navBg : r.schvalene === 0 ? 'rgba(245,158,11,0.16)' : Number(r.bez_dokladu) === 1 ? 'rgba(239,68,68,0.07)' : 'transparent'
  const thS = (right) => ({ position: 'sticky', top: 0, zIndex: 1, background: '#eef5fa', color: COLORS.nav, fontSize: 11, fontWeight: TYPO.weights.bold, textTransform: 'uppercase', letterSpacing: '0.03em', padding: '8px 10px', textAlign: right ? 'right' : 'left', borderBottom: '1px solid #d6e6f2', whiteSpace: 'nowrap' })
  const tfS = (right) => ({ background: '#ecf6ef', color: COLORS.add, fontWeight: TYPO.weights.bold, padding: '7px 10px', textAlign: right ? 'right' : 'left', borderTop: '1px solid #cfe6d6', whiteSpace: 'nowrap' })
  const td = { padding: '6px 10px', borderBottom: `1px solid ${COLORS.lineSoft}`, fontSize: TYPO.sizes.sm, verticalAlign: 'top' }

  const table = (
    <div style={{ border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, background: COLORS.bg, overflow: 'auto', maxHeight: 'calc(100vh - 330px)', minHeight: 320 }}>
      <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%', minWidth: 1100, fontVariantNumeric: 'tabular-nums' }}>
        <thead><tr>
          <th scope="col" style={thS()}>Dátum</th><th scope="col" style={thS()}>Číslo dokladu</th><th scope="col" style={thS()}>Popis</th>
          <th scope="col" style={thS()}>Partner alebo osoba</th><th scope="col" style={thS()}>Zákazka alebo réžia</th>
          <th scope="col" style={thS(true)}>Príjem €</th><th scope="col" style={thS(true)}>Výdavok €</th><th scope="col" style={thS(true)}>Zostatok €</th>
          <th scope="col" style={thS()}>Doklad</th><th scope="col" style={thS()}>Stav</th><th scope="col" aria-label="Akcie" style={{ ...thS(), width: 36 }} />
        </tr></thead>
        <tbody>
          {loading ? <tr><td colSpan={11}>{prazdne('Načítavam…')}</td></tr>
            : filtered.length === 0 ? <tr><td colSpan={11}>{prazdne('Žiadne pohyby pre tento filter.')}</td></tr>
            : filtered.map(r => {
              const skr = skryty(r), pr = jePrijem(r), sel = !!r.id && r.id === selectedId
              return (
                <tr key={r.id || (r.datum + '|' + r.popis + '|' + r.suma)} data-row-id={r.id || undefined} onClick={strazKlik(() => setSelectedId(r.id || null))} onDoubleClick={strazKlik(() => otvor(r))}
                  style={{ cursor: skr ? 'default' : 'pointer', background: pozadie(r, sel) }}>
                  <td style={{ ...td, whiteSpace: 'nowrap', boxShadow: sel ? `inset 3px 0 0 ${COLORS.nav}` : 'none' }}>{SK.date(r.datum) || r.datum}</td>
                  <td style={{ ...td, fontFamily: TYPO.mono, whiteSpace: 'nowrap' }}>{r.id || '—'}</td>
                  <td style={{ ...td, minWidth: 220 }}>{skr ? <span style={{ color: COLORS.muted, fontStyle: 'italic' }}>toner (súkromné — detail len majiteľ)</span> : r.popis}{!skr && r.kategoria ? <span style={{ marginLeft: 6, fontSize: TYPO.sizes.xs, color: COLORS.muted }}>{r.kategoria}</span> : null}</td>
                  <td style={td}>{skr ? '' : osobaBunka(r)}</td>
                  <td style={td}>{skr ? '—' : zakazkaBunka(r)}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: TYPO.weights.bold, color: COLORS.add, whiteSpace: 'nowrap' }}>{!skr && pr ? bezMenyP(Math.abs(Number(r.suma) || 0)) : ''}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: TYPO.weights.bold, color: COLORS.remove, whiteSpace: 'nowrap' }}>{!skr && !pr ? bezMenyP(Math.abs(Number(r.suma) || 0)) : ''}</td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap', color: Number(r.zostatok) < 0 ? COLORS.remove : COLORS.text }}>{skr ? '' : bezMenyP(Number(r.zostatok) || 0)}</td>
                  <td style={{ ...td, fontSize: TYPO.sizes.xs, color: COLORS.muted, maxWidth: 220 }}>{skr ? '' : dokladText(r)}</td>
                  <td style={td}><PoklStavTag stav={stavRiadku(r)} /></td>
                  <td style={{ ...td, padding: '2px 4px', textAlign: 'center' }} onClick={e => e.stopPropagation()} onDoubleClick={e => e.stopPropagation()}>{!skr && <RowMenu compact actions={menuFor(r)} />}</td>
                </tr>
              )
            })}
        </tbody>
        <tfoot><tr>
          <td colSpan={5} style={tfS()}>Spolu · {filtered.length} pohybov</td>
          <td style={tfS(true)}>{bezMenyP(sumy.prijem)}</td><td style={tfS(true)}>{bezMenyP(sumy.vydaj)}</td><td style={tfS(true)}>{bezMenyP(sumy.zostatok)}</td>
          <td colSpan={3} style={{ ...tfS(), fontSize: TYPO.sizes.xs, color: COLORS.muted, fontWeight: 400 }}>zostatok po poslednom pohybe vo výbere</td>
        </tr></tfoot>
      </table>
    </div>
  )
  const list = (
    <div style={{ height: '100%', overflow: 'auto', padding: 6 }}>
      {loading ? prazdne('Načítavam…') : filtered.length === 0 ? prazdne('Žiadne pohyby pre tento filter.') : filtered.map(r => {
        const skr = skryty(r), pr = jePrijem(r)
        return (
          <ListRow key={r.id || (r.datum + '|' + r.popis + '|' + r.suma)} dataId={r.id} selected={!!r.id && r.id === selectedId} onClick={() => setSelectedId(r.id || null)} onDoubleClick={() => otvor(r)} stripColor={pr ? COLORS.add : COLORS.remove}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, alignItems: 'flex-start', background: pozadie(r, false), borderRadius: RADIUS.sm }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: TYPO.weights.semibold, color: COLORS.text, wordBreak: 'break-word' }}>{skr ? 'toner (súkromné)' : r.popis}</div>
                <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, fontFamily: TYPO.mono, marginTop: 2 }}>{SK.date(r.datum)} · {r.id || 'bez čísla'}{!skr && osobaText(r) ? ' · ' + osobaText(r) : ''}</div>
                <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginTop: 2 }}>{skr ? '' : zakazkaBunka(r)}</div>
              </div>
              <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <span style={{ fontWeight: TYPO.weights.bold, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', color: pr ? COLORS.add : COLORS.remove }}>{skr ? '' : (pr ? '+ ' : '− ') + SK.eur(Math.abs(Number(r.suma) || 0))}</span>
                  {!skr && <RowMenu compact actions={menuFor(r)} />}
                </div>
                <PoklStavTag stav={stavRiadku(r)} />
              </div>
            </div>
          </ListRow>
        )
      })}
    </div>
  )
  const pole = (label, value) => (
    <div style={{ background: COLORS.bg, border: `1px solid ${COLORS.lineSoft}`, borderRadius: RADIUS.sm, padding: '6px 10px', minWidth: 0 }}>
      <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: TYPO.sizes.sm, color: COLORS.text, marginTop: 2, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{value}</div>
    </div>
  )
  const detail = selected ? (
    <div style={{ padding: SPACE.md, display: 'flex', flexDirection: 'column', gap: SPACE.sm, height: '100%', overflow: 'auto', background: COLORS.bg, borderRadius: RADIUS.lg }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.sm, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: TYPO.sizes.md, fontWeight: TYPO.weights.extra }}><span style={{ color: COLORS.nav, fontFamily: TYPO.mono }}>{selected.id || 'bez čísla'}</span> · {skryty(selected) ? 'toner' : selected.popis}</h2>
        <PoklStavTag stav={stavRiadku(selected)} />
        <span style={{ flex: 1 }} />
        {!skryty(selected) && <BtnGhost size="sm" onClick={() => otvor(selected)}>{zMiezd(selected) ? 'Otvoriť' : 'Upraviť'}</BtnGhost>}
        {!skryty(selected) && <RowMenu actions={menuFor(selected)} />}
      </div>
      {skryty(selected) ? <div style={{ color: COLORS.muted }}>Toner je súkromný — detail vidí len majiteľ (ústava 11.11).</div> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: SPACE.sm }}>
          {pole('Dátum', SK.date(selected.datum))}
          {pole(jePrijem(selected) ? 'Príjem' : 'Výdavok', SK.eur(Math.abs(Number(selected.suma) || 0)))}
          {pole('Zostatok po pohybe', SK.eur(Number(selected.zostatok) || 0))}
          {pole('Kategória', selected.kategoria || '—')}
          {pole('Partner alebo osoba', osobaBunka(selected) || '—')}
          {pole('Zákazka alebo réžia', zakazkaBunka(selected))}
          {pole('Doklad', dokladText(selected))}
          {pole('Poznámka', selected.poznamka || '—')}
        </div>
      )}
    </div>
  ) : <EmptyPane text="Vyber pohyb v zozname" />

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.md }}>
      <KpiRow cols={4}>
        <KpiCard title="Zostatok v pokladni" value={SK.eur(kpi.zostatok)} color={kpi.zostatok < 0 ? COLORS.remove : COLORS.nav} highlight />
        <KpiCard title={filterMonth ? 'Príjmy ' + MESIACE[filterMonth - 1] : 'Príjmy ' + rok} color={COLORS.add} count={kpi.prijemN} sum={kpi.prijem} onClick={() => setChip('prijmy')} highlight={chip === 'prijmy'} />
        <KpiCard title={filterMonth ? 'Výdavky ' + MESIACE[filterMonth - 1] : 'Výdavky ' + rok} color={COLORS.remove} count={kpi.vydajN} sum={kpi.vydaj} onClick={() => setChip('vydaje')} highlight={chip === 'vydaje'} />
        <KpiCard title="Čaká na schválenie" color={COLORS.total} count={naSchvalenie.count} sum={naSchvalenie.suma} onClick={() => setChip('caka')} highlight={chip === 'caka'} />
      </KpiRow>

      {zamok !== null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.sm, flexWrap: 'wrap', padding: '6px 12px', borderRadius: RADIUS.md, background: zamok ? COLORS.bgSoft : 'rgba(2,132,199,0.08)', border: `1px solid ${zamok ? COLORS.line : '#bae6fd'}`, fontSize: TYPO.sizes.sm }}>
          <b>Výplaty osobám:</b>
          <span>zapisuje ich Lucia tu v Pokladni (osoba = zamestnanec). Riadok v Mzdách sa na pohyb napojí sám — rovnaká suma, ±3 dni, tá istá osoba. Mzdy do Pokladne nezapisujú (Mirko 20. 10. 2026, Princíp pokladne bod 5).</span>
          <span style={{ flex: 1 }} />
        </div>
      )}

      <InterneBezDokladu pinGate={pinGateMain} toast={toast} onChanged={load} />
      {naSchvalenie.count > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.md, padding: '10px 14px', background: 'rgba(245,158,11,0.14)', border: '1px solid #f59e0b', borderLeft: '5px solid #f59e0b', borderRadius: RADIUS.md, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, color: '#b45309' }}>{naSchvalenie.count} výdavkov bez dokladu čaká na schválenie PIN</span>
          <span style={{ fontFamily: TYPO.mono, fontWeight: 700, color: '#b45309' }}>{SK.eur(naSchvalenie.suma)}</span>
          <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>Majiteľ alebo admin schváli cez tri bodky → Schváliť (PIN)</span>
        </div>
      )}

      <FilterChips options={chips} value={chip} onChange={setChip} equalWidth={false} />
      <FilterChips options={mesiace} value={filterMonth} onChange={setFilterMonth} equalWidth={false} />
      <div style={{ display: 'flex', gap: SPACE.sm, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 220, flex: '0 1 340px' }}>
          <SearchInput value={search} onChange={v => setSearch(typeof v === 'string' ? v : (v && v.target ? v.target.value : ''))} dataAttr="data-pokladna-search" placeholder="Hľadať popis, číslo, osobu alebo zákazku (stlač /)…" />
        </div>
        <div role="group" aria-label="Zobrazenie" style={{ display: 'inline-flex', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, overflow: 'hidden' }}>
          {[['tabulka', 'Tabuľka'], ['karty', 'Karty']].map(([k, l]) => (
            <button key={k} type="button" aria-pressed={viewMode === k} onClick={() => setView(k)}
              style={{ padding: '5px 14px', border: 0, background: viewMode === k ? COLORS.nav : COLORS.bg, color: viewMode === k ? '#fff' : COLORS.text, fontFamily: 'inherit', fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, cursor: 'pointer' }}>{l}</button>
          ))}
        </div>
        <span style={{ flex: 1 }} />
        <ExportMenu
          data={filtered.filter(r => !skryty(r))}
          filename={`pokladna-${todayLocal()}`}
          title="Pokladňa — kniha pokladne"
          columns={[
            { key: 'datum', label: 'Dátum' },
            { key: 'id', label: 'Číslo dokladu' },
            { key: 'popis', label: 'Popis' },
            { key: 'kategoria', label: 'Kategória' },
            { key: 'project_id', label: 'Zákazka' },
            { key: 'typ', label: 'Typ' },
            { key: 'suma', label: 'Suma' },
            { key: 'zostatok', label: 'Zostatok' },
          ]}
        />
        <BtnGhost onClick={() => setVbfOpen(true)} title="Ručný výdavok bez dokladu: réžia alebo zákazka, s dôvodom; svieti, kým ho majiteľ neschváli PIN-om">Výdavok bez dokladu</BtnGhost>
        <HelpHint section="87.1" text="ČO: drobný výdavok bez dokladu (napr. upratovačka v hotovosti). PREČO: výnimka — normálne sa výdaj eviduje s dokladom. KAM: odráta sa z pokladne a čaká na schválenie PIN (majiteľ alebo admin)." />
        <BtnAdd onClick={() => setModal({ record: null })} title="Alt+N">+ Nový pohyb</BtnAdd>
      </div>

      {viewMode === 'tabulka' ? table : <div style={{ height: 'calc(100vh - 330px)', minHeight: 420, display: 'flex' }}><MasterDetail left={list} right={detail} leftWidth={380} /></div>}
      <ListStatusBar>↑↓ výber · Enter alebo dvojklik upraviť · / hľadať · Alt+N nový pohyb · tri bodky = akcie · Tabuľka alebo Karty hore</ListStatusBar>

      {modal && (
        <PokladnaModal record={modal.record}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load() }} />
      )}
      {priradit && (
        <PriraditZakazkuModal record={priradit} projekty={projekty} zamestnanci={zamestnanci} partneri={partneri}
          onClose={() => setPriradit(null)}
          onSaved={() => { setPriradit(null); load() }} />
      )}
      {vbfOpen && (
        <VydavokBezDokladuModal onClose={() => setVbfOpen(false)} onSaved={() => { setVbfOpen(false); load() }} />
      )}
    </div>
  )
}

function VydavokBezFaModal({ onClose, onSaved }) {
  const toast = useToast()
  const [suma, setSuma] = useState('')
  const [kategoria, setKategoria] = useState('Iné réžijné náklady')
  const [datum, setDatum] = useState(todayLocal())
  const [poznamka, setPoznamka] = useState('')
  const [busy, setBusy] = useState(false)
  const save = async () => {
    const n = parseFloat(String(suma).replace(',', '.'))
    if (!n || n <= 0) { toast.error('Zadaj sumu'); return }
    setBusy(true)
    try {
      const r = await fetchAuth('/api/pokladna/vydavok-bez-fa', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ suma: n, kategoria, datum, poznamka }) })
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || r.status) }
      toast.success('Zapísané — čaká na schválenie PIN (Majiteľ/Admin)'); onSaved()
    } catch (e) { toast.error('Chyba: ' + e.message) } finally { setBusy(false) }
  }
  const inp = { width: '100%', padding: '8px 10px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, fontFamily: 'inherit', fontSize: TYPO.sizes.base, boxSizing: 'border-box' }
  const lbl = { fontSize: TYPO.sizes.xs, color: COLORS.muted, marginBottom: 4, fontWeight: 600, textTransform: 'uppercase' }
  // DIRTY GUARD (ustava 53.1, 2026-07-16)
  const [gDlg, setGDlg] = useState(false)
  const gDirty = String(suma) !== '' || poznamka !== ''
  const guardClose = () => { if (gDirty) setGDlg(true); else onClose() }
  return (
    <Modal open title="+ Výdavok bez FA (bez dokladu)" onClose={guardClose} width={520}
      footer={<><BtnGhost onClick={guardClose}>Zrušiť</BtnGhost><BtnAdd onClick={save} disabled={busy}>Zapísať (čaká na PIN)</BtnAdd></>}>
      <div style={{ padding: SPACE.sm, background: 'rgba(245,158,11,0.10)', border: '1px solid #f59e0b', borderRadius: RADIUS.sm, fontSize: TYPO.sizes.xs, color: '#b45309', marginBottom: SPACE.md, lineHeight: 1.5 }}>
        Drobný výdavok BEZ dokladu (napr. upratovačka v hotovosti). Zapíše sa a odráta z pokladne, ale <b>svieti</b> kým ho <b>Majiteľ/Admin neschváli PIN-om</b> (ústava 87.1). Default: réžia, hotovosť.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.md }}>
        <div><div style={lbl}>Suma (€) *</div><input style={inp} value={suma} onChange={e => setSuma(e.target.value)} placeholder="napr. 30" inputMode="decimal" autoFocus /></div>
        <div><div style={lbl}>Kategória (réžia)</div>
          <SearchableSelect value={kategoria} onChange={setKategoria} allowCreate options={(COST_CATS.rezia || []).map(k => ({ value: k, label: k }))} placeholder="kategória…" />
        </div>
        <div><div style={lbl}>Dátum</div><input style={inp} type="date" value={datum} onChange={e => setDatum(e.target.value)} /></div>
        <div><div style={lbl}>Poznámka (komu / za čo)</div><input style={inp} value={poznamka} onChange={e => setPoznamka(e.target.value)} placeholder="napr. upratovačka — kancelária" /></div>
      </div>
      <Modal.DirtyConfirm open={gDlg}
        onSave={() => { setGDlg(false); save() }}
        onContinue={() => setGDlg(false)}
        onDiscard={() => { setGDlg(false); onClose() }} />
    </Modal>
  )
}

function PokladnaModal({ record, onClose, onSaved }) {
  // POKLADNA_ZAKAZKA_V1 (20. 10. 2026): Partner alebo osoba, Kam smeruje (zákazka / réžia / presun) a Popis sú povinné (Mirko: „aj zákazka aj réžia“,
  // „patner alebo osoba bude povinný popis tiez“); kategórie výdavku z registra Predkontácie (0.J); presun do/z tonera vytvorí protipohyb v Toneri;
  // sumu, smer a dátum staršieho pohybu mení len admin („prepis sumy iba aktuány den, den spatne iba admin aja“). To isté stráži server.
  const pinGate = usePinGate()
  const toast = useToast()
  const { user } = useAuth()
  const isNew = !record
  const rola = (user && user.role) || ''
  const smieToner = ROLES_TONER.includes(rola)
  // P1.2_USELOCK_2026-05-30 (Mirko, ústava sek. 53.7): zámok cash pohybu pri editácii
  const { lockInfo, acquired } = useLock(isNew ? null : 'pokladna', isNew ? null : record?.id, user)
  const isLocked = !isNew && !acquired && !!lockInfo?.by
  const peniazeOtvorene = isNew || rola === 'admin' || vznikolDnes(record)
  const prepojenyToner = !isNew && !!record.toner_id
  const [form, setForm] = useState(() => isNew ? {
    datum: todayLocal(), druh: 'iny_prijem', popis: '', typ: 'prijem', suma: '',
    cost_center: '', kategoria: 'iny_prijem', project_id: '', osoba: '', poznamka: '', pokyn_soferovi: '',
  } : {
    datum: record.datum || '',
    druh: druhZoZaznamu(record),
    popis: record.popis || '',
    typ: ((record.typ || 'prijem').toLowerCase() === 'prijem') ? 'prijem' : 'vydaj',
    suma: Math.abs(Number(record.suma) || 0),
    cost_center: record.project_id ? 'zakazka' : (record.stredisko || (PRESUN_KAT.includes(String(record.kategoria || '').toLowerCase()) ? 'presun' : '')),
    kategoria: record.kategoria || '',
    project_id: record.project_id || '',
    osoba: record.partner_id ? 'P:' + record.partner_id : (record.employee_id ? 'E:' + record.employee_id : ''),
    poznamka: record.poznamka || '',
    pokyn_soferovi: '',
  })
  const [projects, setProjects] = useState([])
  const [employees, setEmployees] = useState([])
  const [partneri, setPartneri] = useState([])
  const [kategorie, setKategorie] = useState([])
  useEffect(() => {
    fetchAuth('/api/projects').then(r => r.ok ? r.json() : []).then(d => setProjects(poleZoznamu(d))).catch(() => {})
    fetchAuth('/api/employees').then(r => r.ok ? r.json() : []).then(d => setEmployees(poleZoznamu(d))).catch(() => {})
    fetchAuth('/api/customers').then(r => r.ok ? r.json() : []).then(d => setPartneri(poleZoznamu(d))).catch(() => {})
    // jeden register kategórií = Predkontácie (Nastavenia › Účtovanie) — ten istý ako Došlé doklady a export pre účtovníčku (0.J)
    fetchAuth('/api/predkontacie').then(r => r.ok ? r.json() : []).then(d => setKategorie(poleZoznamu(d).filter(k => !(k.active === 0 || k.active === '0')).map(k => k.kategoria).filter(Boolean))).catch(() => {})
  }, [])
  // 15. 9. 2026 (Mirko, súbeh so Sheetom): zámok 11.2 je nastavenie — kým sa stav nenačíta, platí zámok
  const [zamok, setZamok] = useState(true)
  useEffect(() => { fetchAuth('/api/pokladna/nastavenie').then(r => r.ok ? r.json() : null).then(d => { if (d && typeof d.zamok_11_2 === 'boolean') setZamok(d.zamok_11_2) }).catch(() => {}) }, [])

  // HOTOVOST_VAZBA_V1: čakajúce riadky Miezd v hotovosti pre vybranú osobu (len ukazuje; napojenie urobí server po uložení)
  const [mzdyCakaju, setMzdyCakaju] = useState([])
  useEffect(() => {
    const os = String(form.osoba || '')
    if (!os.startsWith('E:')) { setMzdyCakaju([]); return }
    fetchAuth('/api/pokladna/mzdy-cakajuce?employee_id=' + encodeURIComponent(os.slice(2))).then(r => r.ok ? r.json() : []).then(d => setMzdyCakaju(Array.isArray(d) ? d : [])).catch(() => setMzdyCakaju([]))
  }, [form.osoba])

  const jePresunDruh = DRUH_PRESUN.includes(form.druh)
  function setDruh(novyDruh) {
    const info = druhInfo(novyDruh)
    if (!info) return
    setForm(prev => ({
      ...prev,
      druh: novyDruh,
      typ: info.smer,
      kategoria: DRUH_KATEGORIA[novyDruh] || '',
      cost_center: novyDruh === 'rd_platba' ? 'zakazka' : DRUH_PRESUN.includes(novyDruh) ? 'presun' : (prev.cost_center === 'presun' ? '' : prev.cost_center),
      project_id: DRUH_PRESUN.includes(novyDruh) ? '' : prev.project_id,
    }))
  }

  // RDContext_v1 — pri rd_platba fetchne VV context (ústava 11.8 workflow)
  const [rdContext, setRdContext] = useState(null)
  useEffect(() => {
    if (form.druh === 'rd_platba' && form.project_id) {
      fetchAuth(`/api/projects/${form.project_id}/rd-context`)
        .then(r => r.ok ? r.json() : null).then(setRdContext).catch(() => {})
    } else {
      setRdContext(null)
    }
  }, [form.druh, form.project_id])

  const moznosti = useMemo(() => moznostiOsob(employees, partneri), [employees, partneri])
  const menoOsoby = (v) => { const o = moznosti.find(x => x.value === v); return o ? o.label.split(' · ')[0] : '' }
  // Vlákno preview (ústava 11.0): TYP — KTO/ČO — KEDY — ODKAZ
  function vlaknoPopisu(f) {
    const projMeno = (id) => { const p = projects.find(x => x.id === id); return p ? `${id} ${p.name || ''}`.trim() : id }
    const kto = menoOsoby(f.osoba)
    const datum = f.datum || ''
    const kam = f.cost_center === 'zakazka' ? (projMeno(f.project_id) || '(zákazka chýba)') : f.cost_center === 'rezia' ? 'réžia' : ''
    switch (f.druh) {
      case 'rd_platba':   return `RD platba — ${projMeno(f.project_id) || '(zákazka chýba)'} — ${kto || '(klient chýba)'} — ${datum}`
      case 'vklad_banka': return `Presun — výber z banky / ATM — ${kto || '(kto priniesol)'} — ${datum}`
      case 'ekasa':       return `Presun — výber z e-Kasy — predajňa Prešov — ${datum}`
      case 'z_tonera':    return `Presun — z tonera — ${kto || '(kto)'} — ${datum}`
      case 'do_tonera':   return `Presun — do tonera — ${kto || '(kto)'} — ${datum}`
      case 'do_ekasy':    return `Presun — vklad do e-Kasy — ${datum}`
      case 'mzda':        return `Mzda — ${kto || '(osoba chýba)'} — ${datum}${f.project_id ? ' — ' + f.project_id : ''}`
      case 'uhrada_fa':   return `Úhrada FA — ${kto || '(dodávateľ chýba)'} — ${f.popis || ''} — ${kam} — ${datum}`
      case 'blocok':      return `Bloček ${kam || '(kam)'} ${f.kategoria || '(kategória)'} — ${kto || '(kto)'} — ${f.popis || '(popis)'} — ${datum}`
      case 'iny_prijem':  return `Iný príjem — ${kam || '(zákazka alebo réžia)'} — ${kto || '(kto)'} — ${f.popis || '(popis)'} — ${datum}`
      case 'iny_vydaj':   return `${kam || '(zákazka alebo réžia)'} — ${f.kategoria || ''} — ${kto || '(kto)'} — ${f.popis || '(popis)'} — ${datum}`
      default:            return f.popis || ''
    }
  }
  const [busy, setBusy] = useState(false)
  function set(k, v) { setForm(prev => ({ ...prev, [k]: v })) }

  const _s0 = useRef(null); if (_s0.current === null) _s0.current = JSON.stringify(form); useDirtyFlag(JSON.stringify(form) !== _s0.current, { key: 'pokladna-doklad', discard: () => { onClose && onClose() } })   // straz_v4
  async function save() {
    if (!form.datum || !String(form.popis || '').trim() || !form.suma) { toast.error('Vyplň dátum, popis a sumu'); return }
    if (!form.osoba && (isNew || record.partner_id || record.employee_id)) { toast.error('Vyber partnera alebo osobu z registra Matrixu (ústava 11.1)'); return }
    if (!jePresunDruh && !form.cost_center && isNew) { toast.error('Vyber, kam pohyb smeruje: Zákazka alebo Réžia (ústava 11.1)'); return }
    if ((form.cost_center === 'zakazka' || form.druh === 'rd_platba') && !form.project_id) { toast.error(form.druh === 'rd_platba' ? 'RD platba musí mať zákazku (ústava 11.8)' : 'Vyber zákazku'); return }
    if (isNew && form.typ === 'vydaj' && !jePresunDruh && form.druh !== 'mzda' && !form.kategoria) { toast.error('Vyber kategóriu výdavku'); return }
    if ((form.druh === 'z_tonera' || form.druh === 'do_tonera') && !smieToner) { toast.error('Presun s tonerom robí vedúca office, admin alebo majiteľ (ústava 11.11)'); return }
    // PIN guard — veľký výdaj > €8000 (ústava 12.2-F)
    const suma = Math.abs(Number(form.suma) || 0)
    let pinAuditId = null   // p24: server overí schválenie PIN-om
    if (form.typ === 'vydaj' && suma > 8000 && (isNew || peniazeOtvorene)) {
      const gate = await pinGate('velky_vydaj', { suma_EUR: suma, kategoria: form.kategoria || form.druh || '—', popis: (form.popis || '').slice(0, 60) })
      if (!gate || !gate.ok) return  // user zrušil PIN
      pinAuditId = gate.audit_id
    }
    setBusy(true)
    try {
      const vlakno = vlaknoPopisu(form)
      const popisFinal = String(form.popis).trim() || vlakno
      const os = String(form.osoba || '')
      const payload = {
        datum: form.datum,
        popis: popisFinal,
        typ: form.typ === 'vydavok' ? 'vydaj' : form.typ,
        suma,
        kategoria: form.kategoria || DRUH_KATEGORIA[form.druh] || (form.typ === 'prijem' ? 'iny_prijem' : 'ine'),
        // funkcnost_v1: stredisko a druh idú na server (súvis 11.1)
        cost_center: form.cost_center || null,
        stredisko: form.cost_center || '',
        druh: form.druh || null,
        project_id: (form.druh === 'rd_platba' || form.cost_center === 'zakazka') ? (form.project_id || '') : '',
        employee_id: os.startsWith('E:') ? os.slice(2) : '',
        partner_id: os.startsWith('P:') ? os.slice(2) : '',
        pokyn_soferovi: (form.druh === 'rd_platba') ? (form.pokyn_soferovi || '') : null,
        poznamka: (form.poznamka || '') + (isNew && vlakno !== popisFinal ? `\n[vlákno: ${vlakno}]` : ''),
      }
      const url = isNew ? '/api/pokladna' : `/api/pokladna/${record.id}`
      const r = await fetchAuth(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json', ...(pinAuditId ? { 'X-Pin-Audit-Id': String(pinAuditId) } : {}) },
        body: JSON.stringify(payload),
      })
      if (!r.ok) { let m = 'uloženie zlyhalo'; try { const d = await r.json(); m = d.error || m } catch (_) {} throw new Error(m) }
      const odp = await r.json().catch(() => ({}))   // HOTOVOST_VAZBA_V1: server povie, ktoré riadky Miezd sa napojili alebo odpojili
      const mzN = (odp.mzdy_napojene || []).length, mzO = (odp.mzdy_odpojene || []).length
      toast.success((isNew ? (form.druh === 'z_tonera' || form.druh === 'do_tonera' ? 'Pohyb pridaný — aj v Toneri' : 'Pohyb pridaný') : 'Pohyb upravený')
        + (mzN ? ' — napojený riadok Miezd #' + odp.mzdy_napojene.join(', #') : '') + (mzO ? ' — odpojené Mzdy #' + odp.mzdy_odpojene.join(', #') + ' (nezmestia sa), čakajú' : ''))
      onSaved()
    } catch (e) {
      toast.error('Chyba: ' + e.message)
    } finally {
      setBusy(false)
    }
  }

  async function del() {
    if (!confirm(prepojenyToner ? 'Naozaj zmazať tento presun? Zmaže sa aj pohyb v Toneri ' + record.toner_id + '.' : 'Naozaj zmazať tento pohyb?')) return
    setBusy(true)
    try {
      const r = await fetchAuth(`/api/pokladna/${record.id}`, { method: 'DELETE' })
      if (!r.ok) { let m = 'zmazanie zlyhalo'; try { const d = await r.json(); m = d.error || m } catch (_) {} throw new Error(m) }
      toast.error('Zmazané')
      onSaved()
    } catch (e) {
      toast.error('Chyba: ' + e.message)
    } finally {
      setBusy(false)
    }
  }

  // DIRTY GUARD (ustava 53.1, 2026-07-16)
  const [gDlg, setGDlg] = useState(false)
  const initGRef = useRef(JSON.stringify(form))
  const gDirty = JSON.stringify(form) !== initGRef.current
  const guardClose = () => { if (gDirty) setGDlg(true); else onClose() }
  const zakazkyMoznosti = projects.filter(p => form.druh === 'rd_platba' || p.status === 'active' || p.status === 'planned' || !p.status || p.id === form.project_id).map(p => ({ value: p.id, label: p.id + ' · ' + (p.name || '') }))
  const katMoznosti = [...new Set([...(form.kategoria && !kategorie.includes(form.kategoria) && !DRUH_KATEGORIA[form.druh] ? [form.kategoria] : []), ...kategorie])].map(k => ({ value: k, label: k }))
  const druhMoznosti = DRUHY_POHYBOV.filter(d => (!d.legacy || form.druh === d.kod || (d.kod === 'mzda' && !zamok)) && (!d.toner || smieToner || form.druh === d.kod))
    .map(d => ({ value: d.kod, label: d.kod === 'mzda' && !zamok ? 'Výplata alebo záloha osobe (Mzdy sa napoja)' : d.label }))
  const segBtn = (akt) => ({ flex: 1, padding: '10px 12px', border: `2px solid ${akt ? COLORS.nav : COLORS.line}`, borderRadius: RADIUS.md, background: akt ? COLORS.nav : COLORS.bg, color: akt ? '#fff' : COLORS.text, fontWeight: TYPO.weights.semibold, fontSize: TYPO.sizes.sm, fontFamily: 'inherit', cursor: 'pointer' })
  return (
    <Modal open title={isNew ? 'Nový pohyb v pokladni' : `Upraviť — ${record.popis}`}
      onClose={guardClose} width={560}>
      {/* P1.2_USELOCK_2026-05-30: zámok banner ak iný user edituje */}
      {isLocked && (
        <div style={{ background: '#fef2f2', border: `1px solid #ef4444`, borderRadius: RADIUS.md,
          padding: SPACE.sm, marginBottom: SPACE.md, fontSize: TYPO.sizes.sm, color: '#b91c1c' }}>
          Záznam práve upravuje <strong>{lockInfo.by}</strong>. Uloženie je zablokované, aby sa zmeny neprepísali.
        </div>
      )}
      <fieldset disabled={isLocked} style={{ border: 'none', padding: 0, margin: 0,
        opacity: isLocked ? 0.6 : 1, pointerEvents: isLocked ? 'none' : 'auto' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.md }}>
        {!peniazeOtvorene && (
          <div style={{ background: '#FFF7ED', border: '1px solid #FDBA74', borderRadius: RADIUS.md, padding: '8px 12px', fontSize: TYPO.sizes.sm, color: '#7C2D12' }}>
            Pohyb nevznikol dnes — <b>sumu, smer a dátum mení len admin</b> (Mirko, ústava 11.12). Zákazku alebo réžiu, partnera alebo osobu a popis doplníš.
          </div>
        )}
        {prepojenyToner && (
          <div style={{ background: 'rgba(180,83,9,0.08)', border: '1px solid #F59E0B', borderRadius: RADIUS.md, padding: '8px 12px', fontSize: TYPO.sizes.sm }}>
            Presun prepojený s Tonerom ({record.toner_id}). Suma a dátum sa opravia aj v Toneri.
          </div>
        )}
        <Field label="Druh pohybu * (príjem: platba klienta · presun · iný príjem)">
          {peniazeOtvorene && !prepojenyToner
            ? <SearchableSelect value={form.druh} onChange={setDruh} options={druhMoznosti} />
            : <div style={{ ...inputStyle, background: COLORS.bgSoft }}>{(druhInfo(form.druh) || {}).label || form.druh}</div>}
          {druhInfo(form.druh) && (
            <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginTop: 4, fontStyle: 'italic' }}>
              {druhInfo(form.druh).hint}
            </div>
          )}
        </Field>
        {isNew && !zamok && (
          <div style={{ background: 'rgba(2,132,199,0.08)', border: '1px solid #bae6fd', borderRadius: RADIUS.md, padding: '8px 12px', fontSize: TYPO.sizes.sm }}>
            <b>Výplatu alebo zálohu osobe zapisuješ tu</b> — druh „Výplata alebo záloha osobe“ a osoba. Riadok v Mzdách (záloha, Vyplatiť hotovosť) sa na tento pohyb napojí sám: rovnaká suma, ±3 dni, tá istá osoba. Mzdy do Pokladne nezapisujú (Princíp pokladne, bod 5).
          </div>
        )}
        {isNew && zamok && (
          <div style={{ background: '#fff7ed', border: '1px solid #f59e0b', borderRadius: RADIUS.md, padding: '8px 12px', fontSize: TYPO.sizes.sm }}>
            <b>Peniaze pre človeka (mzda, záloha, nafta partii) sa tu nezapisujú.</b> Zapíš ich v Mzdách › kniha partie › Vyplatiť — doklad v pokladni vznikne sám, bez duplicít (ústava 11.2).
            <div style={{ marginTop: 6 }}><a href="/v2/mzdy2026" style={{ color: COLORS.nav, fontWeight: 600 }}>Otvoriť Mzdy › Vyplatiť</a></div>
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACE.md }}>
          <Field label="Dátum *">
            <input type="date" value={form.datum} disabled={!peniazeOtvorene} onChange={e => set('datum', e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Suma (€) *">
            <input type="number" step="0.01" min="0" value={form.suma} disabled={!peniazeOtvorene} onChange={e => set('suma', e.target.value)} style={inputStyle} />
          </Field>
        </div>

        <Field label="Partner alebo osoba * (z registra Matrixu — zamestnanec alebo partner)">
          <SearchableSelect value={form.osoba} onChange={v => set('osoba', v)} options={moznosti} menuPortal
            placeholder="Hľadať zamestnanca alebo partnera…" />
          <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginTop: 4 }}>Kto v registri nie je, založ ho najprv v Partneroch. Voľný text sa nedá (ústava 0.J).</div>
          {mzdyCakaju.length > 0 && (
            <div style={{ marginTop: 6, background: 'rgba(2,132,199,0.08)', border: '1px solid #bae6fd', borderRadius: RADIUS.md, padding: '6px 10px', fontSize: TYPO.sizes.xs }}>
              <b>V Mzdách čaká hotovosť pre túto osobu:</b> {mzdyCakaju.slice(0, 4).map(c => SK.eur(c.suma) + ' (' + String(c.datum).slice(8, 10) + '. ' + String(c.datum).slice(5, 7) + '., ' + (c.typ === 'vratenie_cash' ? 'vrátená záloha' : c.blok) + ')').join(' · ')}. Ak sedí suma a dátum ±3 dni, riadok sa po uložení napojí sám.
            </div>)}
        </Field>

        {jePresunDruh ? (
          <Field label="Kam smeruje">
            <div style={{ ...inputStyle, background: COLORS.bgSoft }}>Presun — {PRESUN_NAZOV[DRUH_KATEGORIA[form.druh]] || ''}: nie je príjem, výdavok ani réžia</div>
          </Field>
        ) : form.druh === 'rd_platba' ? (
          <Field label="Zákazka * (RD vklad MUSÍ byť naviazaný na zákazku, ústava 11.8)">
            <SearchableSelect value={form.project_id} onChange={v => set('project_id', v)} options={zakazkyMoznosti} menuPortal placeholder="Vyber zákazku…" />
          </Field>
        ) : (
          <Field label="Kam smeruje * (zákazka alebo réžia)">
            <div style={{ display: 'flex', gap: SPACE.xs }}>
              <button type="button" onClick={() => set('cost_center', 'zakazka')} style={segBtn(form.cost_center === 'zakazka')}>Na zákazku</button>
              <button type="button" onClick={() => setForm(p => ({ ...p, cost_center: 'rezia', project_id: '' }))} style={segBtn(form.cost_center === 'rezia')}>Réžia (firma)</button>
            </div>
            {form.cost_center === 'zakazka' && (
              <div style={{ marginTop: 8 }}>
                <SearchableSelect value={form.project_id} onChange={v => set('project_id', v)} options={zakazkyMoznosti} menuPortal placeholder="Vyber zákazku…" />
              </div>
            )}
          </Field>
        )}

        {form.typ === 'vydaj' && !jePresunDruh && form.druh !== 'mzda' && (
          <Field label="Kategória * (register Predkontácie — Nastavenia › Účtovanie)">
            <SearchableSelect value={form.kategoria} onChange={v => set('kategoria', v)} options={katMoznosti} menuPortal placeholder="Vyber kategóriu…" />
          </Field>
        )}

        <Field label="Popis *">
          <input type="text" value={form.popis} onChange={e => set('popis', e.target.value)}
            placeholder="napr. tankovanie dodávky VT 998CT, pošta, RD Ľubotice…" style={inputStyle} />
        </Field>
        <Field label="Poznámka">
          <textarea value={form.poznamka} onChange={e => set('poznamka', e.target.value)} rows={2}
            style={{ ...inputStyle, resize: 'vertical' }} />
        </Field>

        {/* RD CONTEXT — VV preview pri RD platbe (ústava 11.8) */}
        {form.druh === 'rd_platba' && rdContext && (
          <div style={{ padding: SPACE.md, background: 'rgba(124,58,237,0.06)', border: `1px solid #7c3aed`, borderRadius: RADIUS.md }}>
            <div style={{ fontSize: TYPO.sizes.xs, color: '#7c3aed', fontWeight: TYPO.weights.bold, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
              Podklady z VV zákazky {rdContext.project.id}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: SPACE.sm, marginBottom: SPACE.sm }}>
              <div><div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>VV suma</div><div style={{ fontFamily: TYPO.mono, fontWeight: TYPO.weights.bold }}>{SK.eur(rdContext.vv_suma)}</div></div>
              <div><div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>Vyfakturované</div><div style={{ fontFamily: TYPO.mono, fontWeight: TYPO.weights.bold }}>{SK.eur(rdContext.fakturovane)}</div></div>
              <div><div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>Zostáva uhradiť</div><div style={{ fontFamily: TYPO.mono, fontWeight: TYPO.weights.bold, color: COLORS.remove }}>{SK.eur(rdContext.zostava_uhradit)}</div></div>
            </div>
            {rdContext.project.rd_paid ? (
              <div style={{ marginTop: SPACE.sm, padding: SPACE.sm, background: COLORS.bg, borderRadius: RADIUS.sm, fontSize: TYPO.sizes.xs, color: COLORS.add }}>Zákazka už má RD platbu (môže byť ukončená)</div>
            ) : (
              <div style={{ marginTop: SPACE.sm, padding: SPACE.sm, background: 'rgba(245,158,11,0.08)', border: `1px solid ${COLORS.total}`, borderRadius: RADIUS.sm, fontSize: TYPO.sizes.xs, color: COLORS.total }}>
                Po uložení tejto RD platby sa zákazka stane <strong>kandidát na ukončenie</strong> (ústava 11.8).
              </div>
            )}
          </div>
        )}

        {/* POKYN ŠOFÉROVI — voliteľný pri RD platbe (anti-strata 11.9) */}
        {isNew && form.druh === 'rd_platba' && (
          <Field label="Pokyn šoférovi (voliteľne) — vytvorí úlohu">
            <textarea value={form.pokyn_soferovi || ''} onChange={e => set('pokyn_soferovi', e.target.value)}
              rows={2} placeholder="napr. Stiahnúť zvyšný materiál + palety zo stavby Trnava Prúdy"
              style={{ ...inputStyle, resize: 'vertical' }} />
          </Field>
        )}

        {/* VLÁKNO PREVIEW — ústava 11.0 */}
        <div style={{ padding: SPACE.md, background: 'rgba(34,197,94,0.06)', border: `1px solid ${COLORS.add}`, borderRadius: RADIUS.md }}>
          <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.add, fontWeight: TYPO.weights.bold, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
            Vlákno (tak bude záznam v pokladni)
          </div>
          <div style={{ fontFamily: TYPO.mono, fontSize: TYPO.sizes.sm, color: COLORS.text }}>{vlaknoPopisu(form)}</div>
        </div>
        <div style={{ display: 'flex', gap: SPACE.sm, justifyContent: 'flex-end' }}>
          {!isNew && peniazeOtvorene && <Btn variant="remove" onClick={del} disabled={busy || isLocked} style={{ marginRight: 'auto' }}>Zmazať</Btn>}
          <BtnGhost onClick={guardClose} disabled={busy}>Zrušiť</BtnGhost>
          <BtnAdd onClick={save} disabled={busy || isLocked}>{busy ? 'Ukladám…' : (isLocked ? 'Zamknuté' : 'Uložiť')}</BtnAdd>
        </div>
      </div>
      </fieldset>
      <Modal.DirtyConfirm open={gDlg}
        onSave={() => { setGDlg(false); save() }}
        onContinue={() => setGDlg(false)}
        onDiscard={() => { setGDlg(false); onClose() }} />
    </Modal>
  )
}

// ─── Tab TONER ────────────────────────────────────────────────────────
function TonerTab({ onZmena } = {}) {
  const toast = useToast()
  const [items, setItems] = useState([])
  const [balance, setBalance] = useState({ zostatok: 0, vklady: 0, vybery: 0 })
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(null)

  function load() {
    setLoading(true)
    Promise.all([
      fetchAuth('/api/toner').then(r => r.ok ? r.json() : []),
      fetchAuth('/api/toner/balance').then(r => r.ok ? r.json() : { zostatok: 0, vklady: 0, vybery: 0 }),
    ]).then(([rows, b]) => {
      setItems(Array.isArray(rows) ? rows : [])
      setBalance(b || { zostatok: 0, vklady: 0, vybery: 0 })
    }).finally(() => { setLoading(false); if (onZmena) onZmena() })
  }
  useEffect(load, [])

  const sorted = useMemo(() => [...items].sort((a,b) => (b.datum || '').localeCompare(a.datum || '')), [items])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.lg }}>
      <div style={{
        padding: SPACE.md,
        background: 'rgba(245,158,11,0.06)',
        border: `1px solid #f59e0b`,
        borderRadius: RADIUS.md,
        fontSize: TYPO.sizes.sm, color: '#f59e0b',
      }}>
        <strong>Toner — súkromný fond mimo firmy.</strong> Evidencia vkladov / výberov. Mimo účtovníctva.
      </div>

      <KpiRow cols={3}>
        <KpiCard title="Aktuálny zostatok" value={SK.eur(balance.zostatok)} color="#f59e0b" highlight />
        <KpiCard title="Vklady (celkom)"   value={SK.eur(balance.vklady)}   color={COLORS.add} />
        <KpiCard title="Výbery (celkom)"   value={SK.eur(balance.vybery)}   color={COLORS.remove} />
      </KpiRow>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <ExportMenu
          data={sorted}  /* FIX 18.7.: bolo `filtered` z PokladnaTab scope → ReferenceError, Toner tab padal */
          filename={`pokladna-${todayLocal()}`}
          title="Pokladňa — hotovostné pohyby"
          columns={[
            { key: 'datum', label: 'Dátum' },
            { key: 'popis', label: 'Popis' },
            { key: 'kategoria', label: 'Kategória' },
            { key: 'project_id', label: 'Zákazka' },
            { key: 'typ', label: 'Typ' },
            { key: 'suma', label: 'Suma' },
            { key: 'zostatok', label: 'Zostatok' },
          ]}
        />
        <BtnAdd onClick={() => setModal({ record: null })}>+ Nový pohyb</BtnAdd>
      </div>

      <div style={{
        background: COLORS.bg, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.lg, overflow: 'hidden',
      }}>
        {loading ? (
          <div style={{ padding: SPACE.xl, color: COLORS.muted, textAlign: 'center' }}>Načítavam…</div>
        ) : sorted.length === 0 ? (
          <div style={{ padding: SPACE.xl, color: COLORS.muted, textAlign: 'center' }}>
            Žiadne pohyby. Pridaj prvý vklad.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: TYPO.sizes.sm }}>
            <thead>
              <tr style={{ background: COLORS.bgSoft, borderBottom: `2px solid ${COLORS.line}` }}>
                <Th>Dátum</Th><Th>Popis</Th><Th>Typ</Th>
                <Th align="right">Suma</Th><Th align="right">Zostatok</Th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(r => (
                <tr key={r.id} onClick={() => setModal({ record: r })}
                  style={{ borderBottom: `1px solid ${COLORS.lineSoft}`, cursor: 'pointer' }}>
                  <td style={tdMono}>{SK.date(r.datum) || r.datum}</td>
                  <td style={tdCell}>{r.popis || '—'}</td>
                  <td style={{ ...tdCell, color: r.typ === 'vklad' ? COLORS.add : COLORS.remove,
                    fontWeight: TYPO.weights.semibold }}>
                    {r.typ === 'vklad' ? 'Vklad' : 'Výber'}
                  </td>
                  <td style={{ ...tdMono, textAlign: 'right', fontWeight: TYPO.weights.bold,
                    color: r.typ === 'vklad' ? COLORS.add : COLORS.remove }}>
                    {r.typ === 'vklad' ? '+' : '−'}{SK.eur(r.suma)}
                  </td>
                  <td style={{ ...tdMono, textAlign: 'right', color: COLORS.muted }}>{SK.eur(r.zostatok)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <TonerModal record={modal.record}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load() }} />
      )}
    </div>
  )
}

function TonerModal({ record, onClose, onSaved }) {
  const toast = useToast()
  const isNew = !record
  const [form, setForm] = useState(() => isNew ? {
    datum: todayLocal(),
    popis: '',
    typ: 'vklad',
    suma: '',
    poznamka: '',
  } : {
    datum: record.datum || '',
    popis: record.popis || '',
    typ: record.typ || 'vklad',
    suma: record.suma || '',
    poznamka: record.poznamka || '',
  })
  const [busy, setBusy] = useState(false)
  function set(k, v) { setForm(prev => ({ ...prev, [k]: v })) }

  const _s0 = useRef(null); if (_s0.current === null) _s0.current = JSON.stringify(form); useDirtyFlag(JSON.stringify(form) !== _s0.current, { key: 'toner-doklad', discard: () => { onClose && onClose() } })   // straz_v4
  async function save() {
    if (!form.datum || !form.popis.trim() || !form.suma) { toast.error('Vyplň všetko'); return }
    setBusy(true)
    try {
      const payload = {
        datum: form.datum,
        popis: form.popis.trim(),
        typ: form.typ,
        suma: Math.abs(Number(form.suma)),
        poznamka: form.poznamka,
      }
      const url = isNew ? '/api/toner' : `/api/toner/${record.id}`
      const r = await fetchAuth(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || 'Uloženie zlyhalo') }   // POKLADNA_ZAKAZKA_V1: hláška servera (starší pohyb, presun z Pokladne)
      toast.success('Uložené')
      onSaved()
    } catch (e) { toast.error(e.message) } finally { setBusy(false) }
  }

  async function del() {
    if (!confirm('Zmazať pohyb?')) return
    setBusy(true)
    try {
      const r = await fetchAuth(`/api/toner/${record.id}`, { method: 'DELETE' })
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || 'Zmazanie zlyhalo') }
      toast.error('Zmazané')
      onSaved()
    } catch (e) { toast.error(e.message) } finally { setBusy(false) }
  }

  // DIRTY GUARD (ustava 53.1, 2026-07-16)
  const [gDlg, setGDlg] = useState(false)
  const initGRef = useRef(JSON.stringify(form))
  const gDirty = JSON.stringify(form) !== initGRef.current
  const guardClose = () => { if (gDirty) setGDlg(true); else onClose() }
  return (
    <Modal open title={isNew ? 'Nový pohyb Toner' : `Upraviť — ${record.popis}`}
      onClose={guardClose} width={480}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.md }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACE.md }}>
          <Field label="Dátum *">
            <input type="date" value={form.datum} onChange={e => set('datum', e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Typ">
            <SearchableSelect value={form.typ} onChange={v => set('typ', v)}
              options={[
                { value: 'vklad', label: 'Vklad' },
                { value: 'vyber', label: 'Výber' },
              ]} />
          </Field>
        </div>
        <Field label="Popis *">
          <input type="text" value={form.popis} onChange={e => set('popis', e.target.value)}
            placeholder="napr. RD Sečovská Polianka, Sponzorské od Ivana…" style={inputStyle} />
        </Field>
        <Field label="Suma (€) *">
          <input type="number" step="0.01" min="0" value={form.suma} onChange={e => set('suma', e.target.value)} style={inputStyle} />
        </Field>
        <Field label="Poznámka">
          <textarea value={form.poznamka} onChange={e => set('poznamka', e.target.value)} rows={2}
            style={{ ...inputStyle, resize: 'vertical' }} />
        </Field>
        <div style={{ display: 'flex', gap: SPACE.sm, justifyContent: 'flex-end' }}>
          {!isNew && <Btn variant="remove" onClick={del} disabled={busy} style={{ marginRight: 'auto' }}>Zmazať</Btn>}
          <BtnGhost onClick={guardClose} disabled={busy}>Zrušiť</BtnGhost>
          <BtnAdd onClick={save} disabled={busy}>{busy ? 'Ukladám…' : 'Uložiť'}</BtnAdd>
        </div>
      </div>
      <Modal.DirtyConfirm open={gDlg}
        onSave={() => { setGDlg(false); save() }}
        onContinue={() => setGDlg(false)}
        onDiscard={() => { setGDlg(false); onClose() }} />
    </Modal>
  )
}

// ikona tlacidlo per riadok (Upravit/Zmazat)
function iconBtnStyle(color) {
  return {
    background: "transparent", border: "none", cursor: "pointer",
    color: color, fontSize: TYPO.sizes.md, padding: "4px 8px",
    fontFamily: "inherit",
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────
const tdCell  = { padding: '8px 10px', fontSize: TYPO.sizes.sm }
const tdMono  = { ...tdCell, fontFamily: TYPO.mono }
const tdMuted = { ...tdCell, color: COLORS.muted, fontSize: TYPO.sizes.xs }
function Th({ children, align = 'left', onClick, style: extraStyle = {} }) {
  return <th onClick={onClick} style={{ ...extraStyle,
    padding: '8px 10px', textAlign: align,
    color: COLORS.muted, fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.semibold,
    textTransform: 'uppercase', letterSpacing: '0.06em',
  }}>{children}</th>
}
const inputStyle = {
  width: '100%', padding: '8px 10px',
  border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm,
  fontSize: TYPO.sizes.sm, background: COLORS.bg, color: COLORS.text,
  boxSizing: 'border-box',
}
function Field({ label, children }) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{
        fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.semibold,
        color: COLORS.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em',
      }}>{label}</div>
      {children}
    </label>
  )
}


// 13. 9. 2026 (bod 6): interné výdavky bez dokladu (bez pokladne) čakajúce na PIN — majiteľ ich schváli tu; hotovostné sú v zozname pokladne (svietia)
function InterneBezDokladu({ pinGate, toast, onChanged }) {
  const { user } = useAuth()
  const [rows, setRows] = useState([])
  const load = () => fetchAuth('/api/vydavky-bez-dokladu?cakaju=1').then(r => r.ok ? r.json() : { rows: [] }).then(d => setRows((d.rows || []).filter(r => !r.pokladna_id))).catch(() => {})
  useEffect(() => { load() }, [])
  if (!rows.length) return null
  const canApprove = user && ['admin', 'owner'].includes(user.role)
  const approve = async (r) => {
    const g = await pinGate('vydaj_bez_dokladu', { suma: r.amount, popis: r.note, info: 'Interný výdavok bez dokladu — ' + (r.dovod || '') })
    if (!g || !g.ok) return
    const rr = await fetchAuth('/api/vydavky-bez-dokladu/' + r.id + '/schvalit', { method: 'POST', headers: { 'X-Pin-Audit-Id': String(g.audit_id || '') } })
    if (!rr.ok) { const d = await rr.json().catch(() => ({})); toast.error(d.error || 'Nedá sa'); return }
    toast.success('Schválené'); load(); onChanged && onChanged()
  }
  return (
    <div style={{ padding: '10px 14px', background: 'rgba(245,158,11,0.10)', border: '1px solid #f59e0b', borderLeft: '5px solid #f59e0b', borderRadius: RADIUS.md }}>
      <div style={{ fontWeight: 700, color: '#b45309', marginBottom: 4 }}>{rows.length} interných výdavkov bez dokladu (bez pokladne) čaká na schválenie PIN</div>
      {rows.map(r => (
        <div key={r.id} style={{ display: 'flex', gap: SPACE.md, alignItems: 'center', fontSize: TYPO.sizes.sm, padding: '3px 0', flexWrap: 'wrap' }}>
          <span style={{ fontFamily: TYPO.mono, fontWeight: 700 }}>{SK.eur(r.amount)}</span>
          <span>{r.note}{r.project_name ? ' · ' + r.projectId + ' ' + r.project_name : ' · réžia'} · {r.date}</span>
          <span style={{ color: COLORS.muted, fontSize: TYPO.sizes.xs }}>dôvod: {r.dovod} · zapísal {r.zapisal}</span>
          {canApprove && <BtnAdd style={{ padding: '2px 10px', fontSize: TYPO.sizes.xs }} onClick={() => approve(r)}>Schváliť (PIN)</BtnAdd>}
        </div>))}
    </div>
  )
}


// POKLADNA_BEZ_ZAKAZKY_V1 — office priradí zákazku pohybu, ktorý ju nemá (RD vklad musí mať zákazku, ústava 11.8).
// Návrh hľadá zhodu slov z popisu s názvom zákazky; nič sa nepriraďuje samo, uloží až človek tlačidlom.
// Server dostane len project_id — suma, dátum, popis a ostatné ostanú, ako sú (nedeštruktívne ukladanie 129.1).
const NAVRH_STOP = new Set(['poter', 'anhydrit', 'omietky', 'omietka', 'etapa', 'prevzala', 'referenciu', 'junior', 'slavik', 'platba', 'doplatok', 'zaloha'])
function slovaNavrhu(t) {
  return new Set(norm(String(t || '')).replace(/[^a-z0-9 ]/g, ' ').split(/\s+/).filter(w => w.length >= 4 && !NAVRH_STOP.has(w)))
}
function PriraditZakazkuModal({ record, projekty, zamestnanci, partneri, onClose, onSaved }) {
  // POKLADNA_ZAKAZKA_V1: jedno okno „Doplniť pohyb“ — kam smeruje (zákazka s návrhom podľa popisu a sumy vyúčtovania, alebo réžia)
  // a partner alebo osoba z registra. Pošle len doplnené polia; sumu ani dátum nemení. Ukladá človek tlačidlom (ústava 138.2).
  const toast = useToast()
  const rd = String(record.kategoria || '').toLowerCase() === 'rd_platba' && String(record.typ || '') === 'prijem'
  const chybaKam = !record.project_id && record.stredisko !== 'rezia' && record.stredisko !== 'presun' && !PRESUN_KAT.includes(String(record.kategoria || '').toLowerCase())
  const chybaOsoba = !record.partner_id && !record.employee_id
  const [kam, setKam] = useState('')
  const [osoba, setOsoba] = useState('')
  const [busy, setBusy] = useState(false)
  const [navrhy, setNavrhy] = useState(null)
  useEffect(() => {
    if (!chybaKam) return
    fetchAuth('/api/pokladna/navrh/' + encodeURIComponent(record.id)).then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d && Array.isArray(d.navrhy)) { setNavrhy(d.navrhy); return }
        const s = slovaNavrhu(record.popis), out = []
        for (const p of projekty) { const ps = slovaNavrhu((p.name || '') + ' ' + (p.site_address || '')); let n = 0; for (const w of s) if (ps.has(w)) n++; if (n) out.push({ project_id: p.id, name: p.name, dovody: ['názov'], body: n }) }
        setNavrhy(out.sort((a, b) => b.body - a.body).slice(0, 3))
      }).catch(() => setNavrhy([]))
  }, [record.id])   // eslint-disable-line react-hooks/exhaustive-deps
  const zakMoznosti = useMemo(() => projekty.map(p => ({ value: p.id, label: p.id + ' · ' + (p.name || '') })), [projekty])
  const osMoznosti = useMemo(() => moznostiOsob(zamestnanci, partneri), [zamestnanci, partneri])
  const ulozit = async () => {
    const telo = {}
    if (chybaKam && kam) { if (kam === 'REZIA') { telo.project_id = ''; telo.stredisko = 'rezia' } else telo.project_id = kam }
    if (chybaOsoba && osoba) { if (osoba.startsWith('E:')) telo.employee_id = osoba.slice(2); else telo.partner_id = osoba.slice(2) }
    if (!Object.keys(telo).length) { toast.error(chybaKam ? 'Vyber zákazku alebo réžiu' : 'Vyber partnera alebo osobu'); return }
    setBusy(true)
    try {
      const r = await fetchAuth('/api/pokladna/' + encodeURIComponent(record.id), { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(telo) })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.error || ('chyba ' + r.status))
      if (telo.project_id && d.project_id !== telo.project_id) throw new Error('Zákazka sa neuložila')
      toast.success(record.id + ' doplnený')
      onSaved()
    } catch (e) { toast.error(e.message) } finally { setBusy(false) }
  }
  const tlac = (akt) => ({ padding: '5px 12px', borderRadius: RADIUS.sm, border: '1px solid ' + (akt ? COLORS.nav : COLORS.line), background: akt ? COLORS.nav : COLORS.navBg, color: akt ? '#fff' : COLORS.nav, fontFamily: 'inherit', fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.semibold, cursor: 'pointer' })
  const nadpis = { fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }
  return (
    <Modal open title={'Doplniť pohyb — ' + record.id} onClose={onClose} width="min(600px, 96vw)" maxWidth={600}
      subtitle={SK.date(record.datum) + ' · ' + record.popis + ' · ' + SK.eur(Math.abs(Number(record.suma) || 0))}
      footer={<><BtnGhost onClick={onClose} disabled={busy}>Zrušiť</BtnGhost><BtnAdd onClick={ulozit} disabled={busy || (!kam && !osoba)}>{busy ? 'Ukladám…' : 'Uložiť'}</BtnAdd></>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.md }}>
        {chybaKam && (
          <div>
            <div style={nadpis}>Kam smeruje {rd ? '— RD platba musí mať zákazku (11.8)' : ''}</div>
            {navrhy === null ? <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>Hľadám návrh…</div> : navrhy.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                {navrhy.map(n => (
                  <button key={n.project_id} type="button" onClick={() => setKam(n.project_id)} style={tlac(kam === n.project_id)}>
                    {n.project_id} · {n.name}<span style={{ fontWeight: 400, opacity: 0.8 }}> — podľa: {(n.dovody || []).join(', ')}</span>
                  </button>
                ))}
              </div>
            )}
            <SearchableSelect value={kam === 'REZIA' ? '' : kam} onChange={setKam} options={zakMoznosti} placeholder="Hľadať zákazku podľa čísla alebo názvu…" menuPortal />
            {!rd && <div style={{ marginTop: 8 }}><button type="button" onClick={() => setKam('REZIA')} style={tlac(kam === 'REZIA')}>Réžia (firma)</button></div>}
          </div>
        )}
        {chybaOsoba && (
          <div>
            <div style={nadpis}>Partner alebo osoba (register Matrixu)</div>
            <SearchableSelect value={osoba} onChange={setOsoba} options={osMoznosti} placeholder="Hľadať zamestnanca alebo partnera…" menuPortal />
          </div>
        )}
        <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>Zmení sa len to, čo tu vyberieš. Suma, dátum a popis ostanú, ako sú.</div>
      </div>
    </Modal>
  )
}

// KNIHA_POKLADNE_13_9: väzba riadku pokladne na zdroj (ústava 11.0 vlákno + 11.1 súvis)
function vazbaRiadku(r) {
  const id = String(r.id || ''); const pz = String(r.poznamka || ''); const kat = String(r.kategoria || '').toLowerCase()
  if (r.toner_id) return 'presun · Toner ' + r.toner_id   // POKLADNA_ZAKAZKA_V1
  if (r.mzdy) return 'Mzdy › riadok #' + String(r.mzdy).split(',').join(', #') + (r.employee_id ? ' · ' + r.employee_id : '')   // HOTOVOST_VAZBA_V1
  if (id.startsWith('PKMZ') || /Mzdy 2026 riadok/i.test(pz)) { const m = pz.match(/riadok #(\d+)/); return 'Mzdy › kniha partie' + (m ? ' (riadok ' + m[1] + ')' : '') + (r.employee_id ? ' · ' + r.employee_id : '') }
  if (id.startsWith('PKBD') || r.doklad_id) return 'Výdavok bez dokladu ' + (r.doklad_id || '') + (r.schvalene === 0 ? ' — čaká PIN' : '')
  if (kat === 'rd_platba') return 'RD platba od zákazníka → zákazka'
  if (kat === 'mzda') return 'mzda — starý zápis' + (r.employee_id ? ' · ' + r.employee_id : '') + (r.payroll_id ? ' · payroll (archív)' : '')
  if (kat === 'atm') return 'presun z účtu (ATM)'
  if (kat === 'ekasa') return 'predajňa eKasa'
  if (kat === 'toner') return 'toner'
  if (kat === 'blocek' || kat === 'blocok') return 'bloček' + (r.project_id ? ' → náklad zákazky' : ' → réžia')
  if (kat === 'material' || kat === 'phm' || kat === 'leasing') return kat + (r.project_id ? ' → zákazka' : ' → réžia')
  const m = pz.match(/\[vlákno: ([^\]]+)\]/); if (m) return m[1].slice(0, 80)
  if (r.employee_id) return 'zamestnanec ' + r.employee_id
  return r.project_id ? 'zákazka' : 'réžia'
}
